import * as THREE from 'three';

// Three.js setup
let scene, camera, renderer, panels = [];
let selectedPanel = null;
let isDetailView = false;
let raycaster, mouse;
let loadedImages = 0;
let expectedSlides = 0;
let mouseWorld = new THREE.Vector3(); // Mouse position in 3D world space
let hoveredPanel = null; // Currently hovered panel for glitch effects
let activeEffects = new Map(); // Track active effects on panels

 let panelWidth = 5;
 let panelHeight = 3.4;

let prevWindowIsDetailView = undefined;
let hadWindowIsDetailView = false;

let prevWindowPixelPresets = undefined;
let hadWindowPixelPresets = false;

let gridData = [];
let sectorColors = {};
let sectorOrder = [];

let gridInitDone = false;
let gridIsRunning = false;
let animationFrameId = null;
let checkDataIntervalId = null;
let randomEffectsIntervalId = null;
let canvasContainerEl = null;

let motionDisabled = false;

let fxSequenceStep = 0;
let fxSequenceNextAt = 0;

let effectsBrowserUI = null;
let effectsBrowserOpen = false;
let effectsBrowserCatalog = null;
let effectsBrowserIndex = 0;

let domRootEl = null;
let stopSlogansCallback = null;
let startSlogansCallback = null;
let assetsBaseUrl = '';

let activeVideoPanels = new Set();

function resolveAssetUrl(path) {
    if (!path) return '';
    const s = String(path);
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) return s;
    const base = assetsBaseUrl || '';
    if (!base) return s;
    if (base.endsWith('/')) return `${base}${s}`;
    return `${base}/${s}`;
}

function setMotionDisabled(next) {
    motionDisabled = !!next;

    if (motionDisabled) {
        try {
            if (randomEffectsIntervalId) {
                clearInterval(randomEffectsIntervalId);
                randomEffectsIntervalId = null;
            }
        } catch (e) {
        }

        try {
            if (activeVideoPanels && activeVideoPanels.forEach) {
                activeVideoPanels.forEach((p) => detachVideoTextureFromPanel(p));
            }
            activeVideoPanels = new Set();
        } catch (e) {
        }

        try {
            activeEffects.clear();
            activeSweepingEffect = null;
            demoSequenceActive = false;
        } catch (e) {
        }

        try {
            pixelRestoreAllTransientStates();
        } catch (e) {
        }

        try {
            const takeover = domRootEl && domRootEl.querySelector ? domRootEl.querySelector('#sectorTakeover') : null;
            if (takeover) takeover.classList.remove('active');
        } catch (e) {
        }
    } else {
        try {
            startRandomEffects();
        } catch (e) {
        }
    }
}

function getVideoUrlFromImageUrl(url) {
    if (!url) return '';
    const s = String(url);
    if (/\.mp4(\?|#|$)/i.test(s)) return s;
    if (/\.(jpe?g|png|webp)(\?|#|$)/i.test(s)) return s.replace(/\.(jpe?g|png|webp)(\?|#|$)/i, '.mp4$2');
    return `${s}.mp4`;
}

function detachVideoTextureFromPanel(panel) {
    if (!panel || !panel.userData) return;
    const state = panel.userData.videoState;
    if (!state) return;

    try {
        if (panel.material && panel.material.uniforms && panel.material.uniforms.tDiffuse) {
            if (panel.userData.imageTexture) {
                panel.material.uniforms.tDiffuse.value = panel.userData.imageTexture;
            }
        }
    } catch (e) {
    }

    try {
        if (state.texture && typeof state.texture.dispose === 'function') state.texture.dispose();
    } catch (e) {
    }

    try {
        if (state.videoEl) {
            state.videoEl.pause();
            state.videoEl.removeAttribute('src');
            state.videoEl.load();
        }
    } catch (e) {
    }

    panel.userData.videoState = null;
    try {
        activeVideoPanels.delete(panel);
    } catch (e) {
    }
}

function attachVideoTextureToPanel(panel) {
    if (!panel || !panel.userData || !panel.userData.item) return;
    if (!isDetailView) return;
    if (panel !== selectedPanel) return;
    if (motionDisabled) return;
    if (panel.userData.videoFailed) return;
    if (panel.userData.videoState) return;

    const imgUrl = panel.userData.imageUrl ? String(panel.userData.imageUrl) : resolveAssetUrl(panel.userData.item.img);
    const videoUrl = getVideoUrlFromImageUrl(imgUrl);
    if (!videoUrl || videoUrl === imgUrl) return;

    try {
        const videoEl = document.createElement('video');
        videoEl.src = videoUrl;
        videoEl.muted = true;
        videoEl.loop = true;
        videoEl.playsInline = true;
        videoEl.preload = 'metadata';

        const videoTexture = new THREE.VideoTexture(videoEl);
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;

        panel.userData.videoState = { videoEl, texture: videoTexture, url: videoUrl };
        activeVideoPanels.add(panel);

        videoEl.addEventListener(
            'error',
            () => {
                panel.userData.videoFailed = true;
                detachVideoTextureFromPanel(panel);
            },
            { once: true }
        );

        if (panel.material && panel.material.uniforms && panel.material.uniforms.tDiffuse) {
            panel.material.uniforms.tDiffuse.value = videoTexture;
        }

        const p = videoEl.play();
        if (p && typeof p.catch === 'function') {
            p.catch(() => {
                panel.userData.videoFailed = true;
                detachVideoTextureFromPanel(panel);
            });
        }
    } catch (e) {
        panel.userData.videoFailed = true;
        detachVideoTextureFromPanel(panel);
    }
}

function getEl(id) {
    if (domRootEl && domRootEl.querySelector) {
        return domRootEl.querySelector(`#${id}`);
    }
    return document.getElementById(id);
}

function setDetailFlag(isDetail) {
    try {
        if (domRootEl && domRootEl.dataset) {
            if (isDetail) domRootEl.dataset.predictionsIsDetailView = '1';
            else domRootEl.dataset.predictionsIsDetailView = '0';
        }
    } catch (e) {
    }
    if (typeof window !== 'undefined') {
        window.isDetailView = !!isDetail;
    }
}

let mitigationBtnEl = null;
let gridBtnEl = null;
let prevBtnEl = null;
let nextBtnEl = null;

// Demo sequence / sweeping animation state (must be declared before resetPanelsForDetailView)
let demoSequenceActive = false;
let demoSequenceIndex = 0;
let activeSweepingEffect = null;
let isReturningToGrid = false; // Flag to prevent animations during return transition

let gridLinesGroup = null;
let gridDimensions = { cols: 0, rows: 0, spacing: 6, startX: 0, startY: 0, labelY: 0 };

let sectorLabelSprites = [];
let lockedSector = null;
let sectorBackplate = null;

let isMitigatedMode = false;

let textPanels = [];
let currentTemplate = null;
let templateCycleInterval = null;
let hoveredTextPanel = null;
let textPanelOriginalState = null;
let hoverDebounceTimer = null;
let isTransitioning = false;

let isPixelEditMode = false;
let pixelIntensityMode = 'soft';
let pixelPalette = [
    null,
    '#FFFF66',
    '#FF6600',
    '#33FF00',
    '#00F3FF',
    '#FFFFFF',
    '#FF3333',
    '#A78BFA',
    '#4ECDC4',
    '#EC4899',
    '#F59E0B',
    '#60A5FA',
    '#FB923C',
    '#94A3B8',
    '#111111',
    '#00FF88'
];
let pixelActiveColorIndex = 1;
let pixelGrid = null;
let pixelIsPainting = false;
let pixelIsErasing = false;
let pixelLastPaintKey = null;
let pixelSuppressClick = false;
let pixelPointerDown = false;
let pixelIntensityBeforeEdit = null;
let pendingPixelPayload = null;
let pixelEditorUI = null;
let pixelEditorLoadedSource = null;
let pixelForceBinaryDisplay = false;

let detailPrevPixelIntensityMode = null;
let detailPrevForceBinaryDisplay = null;

let pixelTransientStack = [];

function pixelPushTransientState() {
    if (!pixelGrid || !pixelGrid.cells) return false;
    pixelTransientStack.push({
        cells: new Uint8Array(pixelGrid.cells),
        intensityMode: pixelIntensityMode,
        forceBinary: pixelForceBinaryDisplay
    });
    return true;
}

function pixelPopTransientState() {
    if (!pixelTransientStack.length) return;
    const prev = pixelTransientStack.pop();
    if (!pixelGrid || !pixelGrid.cells || !prev || !prev.cells) return;
    pixelGrid.cells.set(prev.cells);
    pixelIntensityMode = prev.intensityMode;
    pixelForceBinaryDisplay = prev.forceBinary;
    pixelUpdateAllOverlays();
}

function pixelRestoreAllTransientStates() {
    while (pixelTransientStack.length) {
        pixelPopTransientState();
    }
}

function resetPanelsForGridView() {
    pixelRestoreAllTransientStates();
    activeEffects.clear();
    activeSweepingEffect = null;
    demoSequenceActive = false;

    if (hoveredPanel && hoveredPanel.material && hoveredPanel.material.uniforms) {
        hoveredPanel.material.uniforms.glitchIntensity.value = 0;
        hoveredPanel.material.uniforms.rgbShift.value = 0;
    }
    hoveredPanel = null;

    panels.forEach((p) => {
        if (p && p.material && p.material.uniforms) {
            p.material.uniforms.glitchIntensity.value = 0;
            p.material.uniforms.rgbShift.value = 0;
        }
    });

    if (pixelGrid) {
        pixelUpdateAllOverlays();
    }
}

function setMitigationHeld(nextHeld) {
    const mitigationBtn = getEl('mitigation-btn');
    if (mitigationBtn) {
        if (nextHeld) mitigationBtn.classList.add('held');
        else mitigationBtn.classList.remove('held');
    }

    const hudMitigationToggle = getEl('hud-mitigation-toggle');
    if (hudMitigationToggle) {
        hudMitigationToggle.textContent = nextHeld ? 'ON' : 'OFF';
        if (nextHeld) hudMitigationToggle.classList.add('held');
        else hudMitigationToggle.classList.remove('held');
    }
}

function resetPanelsForDetailView() {
    pixelRestoreAllTransientStates();
    activeEffects.clear();
    activeSweepingEffect = null;
    demoSequenceActive = false;

    if (hoveredPanel && hoveredPanel.material && hoveredPanel.material.uniforms) {
        hoveredPanel.material.uniforms.glitchIntensity.value = 0;
    }
    hoveredPanel = null;

    if (detailPrevPixelIntensityMode === null) detailPrevPixelIntensityMode = pixelIntensityMode;
    if (detailPrevForceBinaryDisplay === null) detailPrevForceBinaryDisplay = pixelForceBinaryDisplay;

    pixelIntensityMode = 'off';
    pixelForceBinaryDisplay = false;

    panels.forEach((p) => {
        if (p && p.userData && p.userData.originalPosition) {
            p.position.set(p.userData.originalPosition.x, p.userData.originalPosition.y, p.userData.originalPosition.z);
        }
        if (p && p.userData && p.userData.originalRotation) {
            p.rotation.set(p.userData.originalRotation.x, p.userData.originalRotation.y, p.userData.originalRotation.z);
        } else if (p) {
            p.rotation.set(0, 0, 0);
        }
        if (p) {
            p.scale.set(1, 1, 1);
        }
        if (p && p.material && p.material.uniforms) {
            p.material.uniforms.glitchIntensity.value = 0;
            p.material.uniforms.rgbShift.value = 0;
        }
        if (p && p.material) {
            p.material.opacity = 1;
            p.material.transparent = true;
        }
    });

    if (pixelGrid) {
        pixelUpdateAllOverlays();
    }
}

function onMitigationButtonClick(e) {
    e.stopPropagation();
    if (isDetailView && selectedPanel) {
        const item = selectedPanel.userData ? selectedPanel.userData.item : null;
        if (!item || item.type !== 'prediction') {
            isMitigatedMode = false;
            setMitigationHeld(false);
            updateMitigationContent(item);
            return;
        }

        isMitigatedMode = !isMitigatedMode;
        setMitigationHeld(isMitigatedMode);
        updateMitigationContent(item);
    }
}

function onGridButtonClick(e) {
    e.stopPropagation();
    returnToGrid();
}

function onPrevButtonClick(e) {
    e.stopPropagation();
    navigateSlide(-1);
}

function onNextButtonClick(e) {
    e.stopPropagation();
    navigateSlide(1);
}

function init({ canvasContainer } = {}) {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.Fog(0x000000, 40, 100);

    camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 0, 40);
    camera.lookAt(0, 0, 0);

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    canvasContainerEl = canvasContainer || canvasContainerEl || getEl('canvas-container');
    if (canvasContainerEl) {
        canvasContainerEl.appendChild(renderer.domElement);
    }

    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    const params = new URLSearchParams(window.location.search);
    pendingPixelPayload = params.get('px') || null;

    createParticles();
    
    // Wait for data to load
    checkDataIntervalId = window.setInterval(() => {
        if (gridData.length > 0) {
            clearInterval(checkDataIntervalId);
            checkDataIntervalId = null;
            createGrid();
        }
    }, 100);

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', onMouseClick);
    window.addEventListener('dblclick', onMouseDblClick);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('contextmenu', onContextMenu);

    mitigationBtnEl = getEl('mitigation-btn');
    if (mitigationBtnEl) mitigationBtnEl.addEventListener('click', onMitigationButtonClick);

    gridBtnEl = getEl('grid-btn');
    if (gridBtnEl) gridBtnEl.addEventListener('click', onGridButtonClick);

    prevBtnEl = getEl('prev-btn');
    if (prevBtnEl) prevBtnEl.addEventListener('click', onPrevButtonClick);

    nextBtnEl = getEl('next-btn');
    if (nextBtnEl) nextBtnEl.addEventListener('click', onNextButtonClick);

    const hudMitigationToggleEl = getEl('hud-mitigation-toggle');
    if (hudMitigationToggleEl) hudMitigationToggleEl.addEventListener('click', onMitigationButtonClick);

    // Start random effect scheduler
    startRandomEffects();

    gridIsRunning = true;
    animate();
}

function updateMouseFromEvent(event) {
    if (!event || !mouse) return;
    const el = (renderer && renderer.domElement) ? renderer.domElement : null;
    if (el && el.getBoundingClientRect) {
        const rect = el.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const w = rect.width || window.innerWidth;
        const h = rect.height || window.innerHeight;
        mouse.x = (x / w) * 2 - 1;
        mouse.y = -(y / h) * 2 + 1;
    } else {
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    }

    // Calculate mouse position in 3D world space for magnetic effect
    if (camera && mouseWorld) {
        const vector = new THREE.Vector3(mouse.x, mouse.y, 0.5);
        vector.unproject(camera);
        const dir = vector.sub(camera.position).normalize();
        const distance = -camera.position.z / dir.z;
        mouseWorld.copy(camera.position).add(dir.multiplyScalar(distance));
    }
}

function resolvePanelFromObject(obj) {
    let cur = obj;
    while (cur) {
        if (cur.userData && cur.userData.item) return cur;
        cur = cur.parent;
    }
    return null;
}

function resolvePanelFromIntersects(intersects) {
    if (!intersects || intersects.length === 0) return null;
    for (let i = 0; i < intersects.length; i++) {
        const hit = intersects[i] && intersects[i].object;
        const panel = resolvePanelFromObject(hit);
        if (panel) return panel;
    }
    return null;
}

function getStoryForMode(item) {
    if (isMitigatedMode && item && item.mitigated_story) return item.mitigated_story;
    return (item && item.story) ? item.story : '';
}

function hudParagraphsFromText(text) {
    const t = (text || '').trim();
    if (!t) return [];
    return t.split(/\n\n+/).map(s => s.trim()).filter(Boolean);
}

function updateMitigationContent(item) {
    const isPrediction = !!(item && item.type === 'prediction');
    if (!isPrediction) {
        isMitigatedMode = false;
        const mitigationBtn = getEl('mitigation-btn');
        if (mitigationBtn) mitigationBtn.classList.remove('held');
    }
    if (selectedPanel && selectedPanel.userData && selectedPanel.userData.item) {
        showHUD(selectedPanel.userData.item, selectedPanel.userData.index);
    } else {
        showHUD(item, 0);
    }
}

function onMouseDblClick(event) {
    if (!isDetailView && isPixelEditMode) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(panels, true);
        if (intersects.length > 0) {
            const panel = intersects[0].object;
            const idx = pixelCellIndexForPanel(panel);
            if (idx !== null) {
                pixelSetCell(panel, 0);
            }
        }
    }
}

function formatCountermeasuresText(item) {
    const cm = item && item.countermeasures ? item.countermeasures : null;
    if (!cm) return '';

    const parts = [];
    parts.push(`COUNTERMEASURES — <span class="cm-status">${cm.status || 'UNKNOWN'}</span>`);
    parts.push(`<span class="cm-window">WINDOW: ${cm.window || 'N/A'}</span>`);
    
    if (cm.actions && cm.actions.length > 0) {
        cm.actions.forEach((action, idx) => {
            const actionText = action.action || action;
            parts.push(`${String(idx + 1).padStart(2, '0')}. ${actionText}`);
        });
    }
    
    return parts.join(' • ');
}

function showHUD(item, index) {
    if (!item) {
        const sectorEl = getEl('hud-sector');
        if (sectorEl) {
            sectorEl.textContent = '';
            sectorEl.style.color = '#FFFF00';
        }

        const signalEl = getEl('hud-signal');
        if (signalEl) signalEl.textContent = '';

        const modeEl = getEl('hud-mode');
        if (modeEl) modeEl.textContent = isMitigatedMode ? 'MODE: MITIGATED' : 'MODE: BASELINE';

        setMitigationHeld(!!isMitigatedMode);

        const titleEl = getEl('hud-title');
        if (titleEl) titleEl.textContent = '';

        const storyEl = getEl('hud-story');
        if (storyEl) storyEl.innerHTML = '';

        const logicEl = getEl('hud-logic');
        if (logicEl) {
            logicEl.textContent = '';
            logicEl.style.display = 'none';
        }

        const signalValidationEl = getEl('hud-signal-validation');
        if (signalValidationEl) signalValidationEl.style.display = 'none';

        const mitigationNote = getEl('hud-mitigation-note');
        if (mitigationNote) mitigationNote.style.display = 'none';

        const cmEl = getEl('hud-countermeasures');
        const cmContainer = getEl('hud-bottom');
        if (cmEl) cmEl.innerHTML = '';
        if (cmContainer) cmContainer.classList.add('hud-hidden');
        return;
    }

    const sectorEl = getEl('hud-sector');
    if (sectorEl) {
        sectorEl.textContent = item.sectorDisplayName || item.sector || '';
        sectorEl.style.color = sectorColors[item.sector] || '#FFFF00';
    }

    const signalEl = getEl('hud-signal');
    if (signalEl) {
        if (item && item.type && item.type !== 'prediction') {
            signalEl.textContent = `PAGE_${String(item.type).toUpperCase()}`;
        } else {
            signalEl.textContent = `SIGNAL_${String((index || 0) + 1).padStart(3, '0')}`;
        }
    }

    const modeEl = getEl('hud-mode');
    if (modeEl) modeEl.textContent = isMitigatedMode ? 'MODE: MITIGATED' : 'MODE: BASELINE';

    setMitigationHeld(!!isMitigatedMode);

    const titleEl = getEl('hud-title');
    if (titleEl) titleEl.textContent = item.title || '';

    const storyEl = getEl('hud-story');
    if (storyEl) {
        const storyText = getStoryForMode(item);
        storyEl.innerHTML = '';
        const parts = hudParagraphsFromText(storyText);
        (parts.length ? parts : [storyText || '']).filter(Boolean).forEach((paraText, idx) => {
            const p = document.createElement('p');
            p.textContent = paraText;
            if (idx > 0) p.style.marginTop = '15px';
            storyEl.appendChild(p);
        });
    }

    const logicEl = getEl('hud-logic');
    if (logicEl) {
        const logicText = (item && item.type === 'prediction' && item.logic) ? String(item.logic) : '';
        if (logicText) {
            logicEl.textContent = logicText;
            logicEl.style.display = 'block';
        } else {
            logicEl.textContent = '';
            logicEl.style.display = 'none';
        }
    }

    const signalValidationEl = getEl('hud-signal-validation');
    if (signalValidationEl && item && item.type === 'prediction' && item.signal_validation) {
        signalValidationEl.innerHTML = String(item.signal_validation);
        signalValidationEl.style.display = 'block';
    } else if (signalValidationEl) {
        signalValidationEl.style.display = 'none';
        signalValidationEl.innerHTML = '';
    }

    const mitigationNote = getEl('hud-mitigation-note');
    if (mitigationNote && item && item.type === 'prediction' && item.mitigated_story) {
        mitigationNote.style.display = 'block';
    } else if (mitigationNote) {
        mitigationNote.style.display = 'none';
    }

    const cmText = (item && item.type === 'prediction') ? formatCountermeasuresText(item) : '';
    const cmEl = getEl('hud-countermeasures');
    const cmContainer = getEl('hud-bottom');
    if (cmEl && cmContainer) {
        cmEl.innerHTML = cmText;
        if (cmText) cmContainer.classList.remove('hud-hidden');
        else cmContainer.classList.add('hud-hidden');
    }

    const ctaActionsEl = getEl('hud-cta-actions');
    if (ctaActionsEl) {
        ctaActionsEl.innerHTML = '';
        if (item && item.type === 'cta' && item.cta && typeof item.cta === 'object') {
            const reportLink = item.cta.report_link || '#';
            const consultLink = item.cta.consultation_link || '#';

            const reportAnchor = document.createElement('a');
            reportAnchor.href = reportLink;
            reportAnchor.textContent = 'View report';
            reportAnchor.className = 'hud-cta-button hud-cta-report';

            const consultAnchor = document.createElement('a');
            consultAnchor.href = consultLink;
            consultAnchor.textContent = 'Book consultation';
            consultAnchor.className = 'hud-cta-button hud-cta-consult';

            ctaActionsEl.appendChild(reportAnchor);
            ctaActionsEl.appendChild(consultAnchor);
        }
    }

    const sidebarEl = domRootEl && domRootEl.querySelector
        ? domRootEl.querySelector('.dashboard-sidebar')
        : null;
    if (sidebarEl) sidebarEl.scrollTop = 0;

    const overlayEl = getEl('hud-overlay');
    if (overlayEl) overlayEl.classList.add('active');

    const controlDeck = getEl('control-deck');
    if (controlDeck) controlDeck.style.display = 'none';

    updateNavButtons();
}

function applySectorLock(sector) {
    lockedSector = sector;
    if (sectorBackplate) {
        scene.remove(sectorBackplate);
        sectorBackplate = null;
    }

    const sectorColor = sectorColors[sector] || '#FFFF00';
    const width = gridDimensions.spacing * 1.6;
    const height = (gridDimensions.rows - 1) * gridDimensions.spacing + 10;
    const geometry = new THREE.PlaneGeometry(width, height);
    const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(sectorColor),
        transparent: true,
        opacity: 0.08,
        side: THREE.DoubleSide
    });

    const colPanels = panels.filter(p => p.userData && p.userData.sector === sector);
    const centerX = colPanels.length > 0 ? colPanels[0].userData.originalPosition.x : 0;
    sectorBackplate = new THREE.Mesh(geometry, material);
    sectorBackplate.position.set(centerX, 0, -3);
    sectorBackplate.userData.isSectorBackplate = true;
    scene.add(sectorBackplate);

    sectorLabelSprites.forEach(sprite => {
        if (sprite.userData.sector === sector) {
            animateScale(sprite, { x: 5.4, y: 1.25, z: 1 }, 600);
            animateOpacity(sprite.material, 1, 600);
        } else {
            animateOpacity(sprite.material, 0.05, 600);
        }
    });
}

function clearSectorLock() {
    lockedSector = null;
    if (sectorBackplate) {
        scene.remove(sectorBackplate);
        sectorBackplate = null;
    }
}

function createGrid() {
    console.log('Creating grid with', gridData.length, 'items');
    
    const spacing = 5.4;

    expectedSlides = gridData.length;
    
    // Group data by sector
    const sectorGroups = {};
    gridData.forEach(item => {
        if (!sectorGroups[item.sector]) {
            sectorGroups[item.sector] = [];
        }
        sectorGroups[item.sector].push(item);
    });
    
    // Get unique sectors in order they appear
    const sectors = (typeof sectorOrder !== 'undefined' && Array.isArray(sectorOrder) && sectorOrder.length > 0)
        ? sectorOrder.filter(s => sectorGroups[s])
        : Object.keys(sectorGroups);
    console.log('Sectors found:', sectors);
    
    const cols = sectors.length;
    const maxRows = Math.max(...Object.values(sectorGroups).map(g => g.length));
    const startX = -(cols - 1) * spacing / 2;
    // Keep the grid vertically centered in the viewport
    const startY = (maxRows - 1) * spacing / 2 - 1;

    gridDimensions = {
        cols,
        rows: maxRows,
        spacing,
        startX,
        startY,
        labelY: startY + spacing * 1.45
    };

    createGridLines(cols, maxRows, spacing);
    
    const sectorColumns = {};

    let panelIndexCounter = 0;
    
    // Create grid organized by sector
    sectors.forEach((sector, colIndex) => {
        const x = startX + colIndex * spacing;
        const sectorColor = sectorColors[sector] || '#FFFF00';
        sectorColumns[sector] = { col: colIndex, x, color: sectorColor };
        
        const items = sectorGroups[sector];
        items.forEach((item, rowIndex) => {
            const y = startY - rowIndex * spacing;
            const z = (Math.random() - 0.5) * 1;

            const panelIndex = panelIndexCounter;
            panelIndexCounter++;

            const textureLoader = new THREE.TextureLoader();
            const createPanelFromTexture = (texture) => {
                const geometry = new THREE.PlaneGeometry(panelWidth, panelHeight);
                
                // Create glitch shader material
                const material = new THREE.ShaderMaterial({
                    uniforms: {
                        tDiffuse: { value: texture },
                        time: { value: 0 },
                        glitchIntensity: { value: 0 },
                        rgbShift: { value: 0 },
                        panelOpacity: { value: 1 },
                        invertAmount: { value: 0 }
                    },
                    vertexShader: `
                        varying vec2 vUv;
                        void main() {
                            vUv = uv;
                            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                        }
                    `,
                    fragmentShader: `
                        uniform sampler2D tDiffuse;
                        uniform float time;
                        uniform float glitchIntensity;
                        uniform float rgbShift;
                        uniform float panelOpacity;
                        uniform float invertAmount;
                        varying vec2 vUv;
                        
                        float random(vec2 co) {
                            return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
                        }
                        
                        void main() {
                            vec2 uv = vUv;
                            
                            // Scanline effect
                            float scanline = sin(uv.y * 800.0 + time * 10.0) * 0.04 * glitchIntensity;
                            
                            // RGB split effect
                            float shift = rgbShift * glitchIntensity * 0.01;
                            vec4 r = texture2D(tDiffuse, uv + vec2(shift, 0.0));
                            vec4 g = texture2D(tDiffuse, uv);
                            vec4 b = texture2D(tDiffuse, uv - vec2(shift, 0.0));
                            
                            // Digital noise
                            float noise = random(uv + time) * 0.1 * glitchIntensity;
                            
                            vec4 color = vec4(r.r, g.g, b.b, 1.0);
                            color.rgb += scanline + noise;
                            color.rgb = mix(color.rgb, vec3(1.0) - color.rgb, clamp(invertAmount, 0.0, 1.0));
                            
                            gl_FragColor = vec4(color.rgb, clamp(panelOpacity, 0.0, 1.0));
                        }
                    `,
                    side: THREE.DoubleSide,
                    transparent: true,
                    opacity: 1.0
                });
                
                const panel = new THREE.Mesh(geometry, material);
                panel.position.set(x, y, z);
                
                // Add visual effect overlays for detail view
                panel.userData.hasVisualEffects = false;
                
                // Parse nodes from logic field
                const nodes = extractNodes(item.logic);
                
                // Individual panel animation data
                panel.userData = {
                    index: panelIndex,
                    sector: sector,
                    sectorColumn: colIndex,
                    rowInSector: rowIndex,
                    originalPosition: { x, y, z },
                    originalRotation: { x: 0, y: 0, z: 0 },
                    item: item,
                    color: sectorColor,
                    nodes: nodes,
                    floatSpeed: 0.5 + Math.random() * 0.5,
                    floatOffset: Math.random() * Math.PI * 2,
                    floatAmplitude: 0.1 + Math.random() * 0.1,
                    driftSpeed: 0.3 + Math.random() * 0.3,
                    driftOffset: Math.random() * Math.PI * 2,
                    imageTexture: texture,
                    imageUrl: resolveAssetUrl(item.img),
                    videoState: null,
                    videoFailed: false
                };

                const overlayGeometry = new THREE.PlaneGeometry(panelWidth, panelHeight);
                const overlayMaterial = new THREE.MeshBasicMaterial({
                    color: 0x000000,
                    transparent: true,
                    opacity: 0,
                    side: THREE.DoubleSide,
                    depthTest: false,
                    depthWrite: false
                });
                const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
                overlay.position.set(0, 0, 0.02);
                overlay.renderOrder = 10;
                overlay.userData.isPixelOverlay = true;
                panel.add(overlay);
                panel.userData.pixelOverlay = overlay;

                const borderGeometry = new THREE.EdgesGeometry(geometry);
                const borderMaterial = new THREE.LineBasicMaterial({
                    color: new THREE.Color(sectorColor),
                    linewidth: 3
                });
                const border = new THREE.LineSegments(borderGeometry, borderMaterial);
                panel.add(border);

                addCornerRivets(panel, panelWidth, panelHeight);

                scene.add(panel);
                panels.push(panel);
                loadedImages++;
                updateLoadingProgress();
                
                if (loadedImages === expectedSlides) {
                    createSectorLabels(sectorColumns, gridDimensions.labelY);
                    createNodeConnections();
                    pixelInitAfterGridReady();
                }
            };

            textureLoader.load(
                resolveAssetUrl(item.img),
                (texture) => {
                    createPanelFromTexture(texture);
                },
                undefined,
                () => {
                    textureLoader.load(resolveAssetUrl('images/title.jpg'), (fallbackTexture) => {
                        createPanelFromTexture(fallbackTexture);
                    });
                }
            );
        });
    });
}

function pixelInitAfterGridReady() {
    if (pixelGrid) return;

    const cols = gridDimensions.cols || 0;
    const rows = gridDimensions.rows || 0;
    if (!cols || !rows) return;

    pixelGrid = {
        v: 1,
        cols,
        rows,
        cells: new Uint8Array(cols * rows)
    };

    const preset = pixelPresetsResolveFromUrl();
    if (preset) {
        pixelApplyPreset(preset);
    } else if (pendingPixelPayload) {
        const decoded = pixelDecodePxPayload(pendingPixelPayload);
        if (decoded) {
            pixelApplyPreset(decoded);
        }
    }

    pixelUpdateAllOverlays();
    pixelEnsureUI();
}

function pixelPresetsResolveFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const pxid = params.get('pxid');
    if (!pxid) return null;

    const presets = window.pixelPresets || {};
    const preset = presets[pxid];
    if (!preset) return null;
    return preset;
}

function pixelApplyPreset(preset) {
    if (!pixelGrid) return;
    if (!preset || preset.v !== 1) return;

    const srcCols = preset.cols;
    const srcRows = preset.rows;
    if (srcCols !== pixelGrid.cols) return;

    const srcLen = srcCols * srcRows;
    let srcCells = null;
    let srcLayout = 'col-major';
    if (preset.cells instanceof Uint8Array) {
        srcCells = preset.cells;
    } else if (Array.isArray(preset.cellsRows)) {
        const rows = preset.cellsRows;
        if (rows.length === srcRows) {
            const arr = [];
            for (let r = 0; r < rows.length; r++) {
                const row = Array.isArray(rows[r]) ? rows[r] : null;
                if (!row || row.length !== srcCols) return;
                for (let c = 0; c < srcCols; c++) arr.push((row[c] | 0) & 0x0f);
            }
            if (arr.length === srcLen) {
                srcCells = new Uint8Array(srcLen);
                for (let i = 0; i < srcLen; i++) srcCells[i] = arr[i] & 0x0f;
                srcLayout = 'row-major';
            }
        }
    } else if (Array.isArray(preset.cellsArray)) {
        const arr = preset.cellsArray;
        if (arr.length === srcLen) {
            srcCells = new Uint8Array(srcLen);
            for (let i = 0; i < srcLen; i++) {
                srcCells[i] = (arr[i] | 0) & 0x0f;
            }
            srcLayout = 'row-major';
        }
    } else if (typeof preset.cells === 'string') {
        srcCells = pixelDecodeCells(preset.cells, srcLen) || pixelDecodeHexCells(preset.cells, srcLen);
        if (srcCells && pixelDecodeHexCells(preset.cells, srcLen)) {
            srcLayout = 'row-major';
        }
    }
    if (!srcCells || srcCells.length !== srcLen) return;

    if (srcLayout === 'row-major') {
        const converted = pixelConvertRowMajorToColMajor(srcCells, srcCols, srcRows);
        if (!converted) return;
        srcCells = converted;
    }

    const remapped = pixelRemapCells(srcCells, srcCols, srcRows, pixelGrid.cols, pixelGrid.rows);
    if (!remapped || remapped.length !== pixelGrid.cells.length) return;
    pixelGrid.cells.set(remapped);
    if (preset.intensity && (preset.intensity === 'soft' || preset.intensity === 'hard' || preset.intensity === 'off')) {
        pixelIntensityMode = preset.intensity;
    }
}

function pixelConvertRowMajorToColMajor(srcCells, cols, rows) {
    if (!srcCells || !cols || !rows) return null;
    if (srcCells.length !== cols * rows) return null;
    const out = new Uint8Array(cols * rows);
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const srcIdx = r * cols + c;
            const dstIdx = c * rows + r;
            out[dstIdx] = srcCells[srcIdx] | 0;
        }
    }
    return out;
}

function pixelCellIndexForPanel(panel) {
    if (!pixelGrid || !panel || !panel.userData) return null;
    const col = panel.userData.sectorColumn;
    const row = panel.userData.rowInSector;
    if (typeof col !== 'number' || typeof row !== 'number') return null;
    if (col < 0 || row < 0 || col >= pixelGrid.cols || row >= pixelGrid.rows) return null;
    return col * pixelGrid.rows + row;
}

function pixelSetCell(panel, colorIndex) {
    if (!pixelGrid) return;
    const idx = pixelCellIndexForPanel(panel);
    if (idx === null) return;
    const clamped = Math.max(0, Math.min(15, colorIndex | 0));
    pixelGrid.cells[idx] = clamped;
    pixelUpdateOverlayForPanel(panel);
}

function pixelOpacityForMode() {
    let base = 0;
    if (pixelIntensityMode === 'hard') base = 0.75;
    else if (pixelIntensityMode === 'soft') base = 0.18;
    else base = 0;
    if (isPixelEditMode && base > 0) {
        return 0.9;
    }
    return base;
}

function pixelUnderlyingOpacityForMode() {
    if (isPixelEditMode) return 0.2;
    return 1.0;
}

function pixelUpdateOverlayForPanel(panel) {
    if (!pixelGrid || !panel || !panel.userData || !panel.userData.pixelOverlay) return;
    const overlay = panel.userData.pixelOverlay;
    const cellIdx = pixelCellIndexForPanel(panel);
    if (cellIdx === null) return;
    const paletteIndex = pixelGrid.cells[cellIdx] | 0;

    if (isPixelEditMode || pixelForceBinaryDisplay) {
        if (paletteIndex === 0 || !pixelPalette[paletteIndex]) {
            overlay.material.opacity = 0;
            panel.material.opacity = 1;
            panel.material.transparent = true;
            return;
        }

        overlay.material.color.set(pixelPalette[paletteIndex]);
        overlay.material.opacity = 1;
        overlay.material.transparent = true;
        panel.material.opacity = 0;
        panel.material.transparent = true;
        return;
    }

    const opacityBase = pixelOpacityForMode();
    if (pixelIntensityMode === 'off' || paletteIndex === 0 || !pixelPalette[paletteIndex]) {
        overlay.material.opacity = 0;
        panel.material.opacity = 1;
        panel.material.transparent = true;
        return;
    }

    overlay.material.color.set(pixelPalette[paletteIndex]);
    overlay.material.opacity = opacityBase;
    overlay.material.transparent = true;
    panel.material.opacity = pixelUnderlyingOpacityForMode();
    panel.material.transparent = true;
}

function pixelUpdateAllOverlays() {
    if (!pixelGrid) return;
    panels.forEach(panel => pixelUpdateOverlayForPanel(panel));
}

function pixelToggleEditMode() {
    if (!pixelGrid || isDetailView) return;
    isPixelEditMode = !isPixelEditMode;
    pixelIsPainting = false;
    pixelIsErasing = false;
    pixelLastPaintKey = null;
    pixelEnsureUI();
    pixelUpdateUI();
    if (pixelEditorUI) {
        pixelEditorUI.style.display = isPixelEditMode ? 'block' : 'none';
    }
    if (isPixelEditMode) {
        if (pixelIntensityBeforeEdit === null) {
            pixelIntensityBeforeEdit = pixelIntensityMode;
        }
        if (pixelIntensityMode === 'off') {
            pixelIntensityMode = 'soft';
        }
        pixelUpdateAllOverlays();
        pixelUpdateUI();
    } else {
        if (pixelIntensityBeforeEdit !== null) {
            pixelIntensityMode = pixelIntensityBeforeEdit;
            pixelIntensityBeforeEdit = null;
            pixelUpdateAllOverlays();
            pixelUpdateUI();
        }
    }
    if (!isPixelEditMode) {
        (domRootEl || document.body).style.cursor = 'crosshair';
    }
}

function pixelCycleIntensity() {
    if (pixelIntensityMode === 'off') pixelIntensityMode = 'soft';
    else if (pixelIntensityMode === 'soft') pixelIntensityMode = 'hard';
    else pixelIntensityMode = 'off';
    pixelUpdateAllOverlays();
    pixelUpdateUI();
}

function pixelEnsureUI() {
    if (pixelEditorUI) return;
    const el = document.createElement('div');
    el.id = 'pixel-editor-ui';
    el.style.position = 'fixed';
    el.style.left = '20px';
    el.style.bottom = '100px';
    el.style.zIndex = '999';
    el.style.background = 'rgba(0,0,0,0.9)';
    el.style.border = '2px solid #FFFF00';
    el.style.padding = '10px 12px';
    el.style.fontFamily = 'Courier New, monospace';
    el.style.fontSize = '12px';
    el.style.color = '#FFFF00';
    el.style.display = 'none';
    el.style.minWidth = '260px';
    el.innerHTML = `
        <div style="font-weight:bold; margin-bottom:6px;">PIXEL EDIT MODE</div>
        <div id="pixel-editor-status" style="opacity:0.9"></div>
        <div style="margin-top:8px; display:flex; gap:6px; align-items:center;">
            <select id="pixel-editor-source" style="flex:1; background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 4px; font-family:Courier New, monospace; font-size:12px;"></select>
            <button id="pixel-editor-load" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">LOAD</button>
        </div>
        <div style="margin-top:6px; display:flex; gap:6px; flex-wrap:wrap;">
            <button id="pixel-editor-save" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">SAVE</button>
            <button id="pixel-editor-add" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">ADD NEW</button>
            <button id="pixel-editor-copy-preset" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">COPY PRESET</button>
            <button id="pixel-editor-copy-retro" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">COPY RETRO</button>
        </div>
        <div style="margin-top:8px; display:flex; gap:6px; flex-wrap:wrap;" id="pixel-editor-palette"></div>
        <div style="margin-top:8px; display:flex; gap:6px; align-items:center;">
            <input id="pixel-editor-marquee-text" type="text" placeholder="Marquee text" style="flex:1; min-width:0; background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 4px; font-family:Courier New, monospace; font-size:12px;" />
            <button id="pixel-editor-marquee-run" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:2px 6px; cursor:pointer;">MARQUEE</button>
        </div>
        <div style="margin-top:8px; opacity:0.8; line-height:1.4;">
            <div>CTRL+SHIFT+P toggle</div>
            <div>Tap paints • Double-tap erases • Drag paints • SHIFT erase</div>
            <div>SHIFT+I intensity • SHIFT+E export JSON • SHIFT+U copy px URL</div>
        </div>
    `;
    const mountTarget = domRootEl || document.body;
    mountTarget.appendChild(el);
    pixelEditorUI = el;
    pixelBuildPaletteUI();
    pixelWireEditorControls();
    pixelRefreshEditorSources();
    pixelUpdateUI();
}

function pixelWireEditorControls() {
    const loadBtn = getEl('pixel-editor-load');
    if (loadBtn) {
        loadBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelLoadSelectedEditorSource();
        });
    }

    const saveBtn = getEl('pixel-editor-save');
    if (saveBtn) {
        saveBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelSaveToLoadedSource();
        });
    }

    const addBtn = getEl('pixel-editor-add');
    if (addBtn) {
        addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelAddNewSourceFromCurrent();
        });
    }

    const copyPresetBtn = getEl('pixel-editor-copy-preset');
    if (copyPresetBtn) {
        copyPresetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelCopyPresetSnippet();
        });
    }

    const copyRetroBtn = getEl('pixel-editor-copy-retro');
    if (copyRetroBtn) {
        copyRetroBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelCopyRetroIconSnippet();
        });
    }

    const marqueeBtn = getEl('pixel-editor-marquee-run');
    const marqueeInput = getEl('pixel-editor-marquee-text');
    const triggerMarquee = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const msg = marqueeInput ? marqueeInput.value.trim() : '';
        pixelTriggerMarquee(msg);
    };
    if (marqueeBtn) {
        marqueeBtn.addEventListener('click', triggerMarquee);
    }
    if (marqueeInput) {
        marqueeInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                triggerMarquee(e);
            }
        });
    }
}

function pixelBuildPaletteUI() {
    const holder = getEl('pixel-editor-palette');
    if (!holder) return;
    holder.innerHTML = '';
    for (let i = 0; i < pixelPalette.length; i++) {
        const sw = document.createElement('button');
        sw.type = 'button';
        sw.style.width = '18px';
        sw.style.height = '18px';
        sw.style.border = '1px solid #FFFF00';
        sw.style.background = pixelPalette[i] ? pixelPalette[i] : 'transparent';
        sw.style.padding = '0';
        sw.style.cursor = 'pointer';
        sw.title = String(i);
        sw.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            pixelActiveColorIndex = i;
            pixelUpdateUI();
        });
        holder.appendChild(sw);
    }
}

function pixelUpdateUI() {
    if (!pixelEditorUI) return;

    const status = getEl('pixel-editor-status');
    if (status) {
        const src = (typeof pixelEditorLoadedSource === 'string' && pixelEditorLoadedSource)
            ? `  Source: ${pixelEditorLoadedSource}`
            : '';
        status.textContent = `Color: ${pixelActiveColorIndex}  Intensity: ${pixelIntensityMode}${src}`;
    }

    const holder = getEl('pixel-editor-palette');
    if (holder) {
        [...holder.children].forEach((child, idx) => {
            child.style.outline = idx === pixelActiveColorIndex ? '2px solid #FF6600' : 'none';
        });
    }

    const sel = getEl('pixel-editor-source');
    if (sel && typeof pixelEditorLoadedSource === 'string' && pixelEditorLoadedSource) {
        if (sel.value !== pixelEditorLoadedSource) {
            sel.value = pixelEditorLoadedSource;
        }
    }
}

function pixelTriggerMarquee(message) {
    if (motionDisabled) return;
    if (isDetailView) return;
    if (activeSweepingEffect) return;
    const eff = effectPixelMarquee(message);
    if (eff) activeSweepingEffect = eff;
}

function pixelCopyPresetSnippet() {
    const preset = pixelExportPreset();
    if (!preset) return;
    let suggested = 'my_preset';
    if (typeof pixelEditorLoadedSource === 'string' && pixelEditorLoadedSource.startsWith('preset:')) {
        suggested = pixelEditorLoadedSource.slice('preset:'.length) || suggested;
    }
    const id = window.prompt('Preset id to export (for pixel-presets.js):', suggested);
    if (!id) return;
    const snippet = `window.pixelPresets = window.pixelPresets || {};
window.pixelPresets[${JSON.stringify(id)}] = ${JSON.stringify({ v: 1, cols: preset.cols, rows: preset.rows, intensity: preset.intensity, cells: preset.cells }, null, 2)};
`;
    pixelCopyText(snippet);
}

function pixelCopyRetroIconSnippet() {
    if (!pixelGrid) return;
    let suggested = 'new_icon_12x7';
    if (typeof pixelEditorLoadedSource === 'string' && pixelEditorLoadedSource.startsWith('retro:')) {
        suggested = pixelEditorLoadedSource.slice('retro:'.length) || suggested;
    }
    const name = window.prompt('retroIcons key to export:', suggested);
    if (!name) return;
    const icon = pixelEncodeCellsToRetroIcon(pixelGrid.cells, pixelGrid.cols, pixelGrid.rows);
    if (!icon) return;
    const snippet = `${JSON.stringify(name)}: ${JSON.stringify(icon, null, 2)},\n`;
    pixelCopyText(snippet);
}

function pixelSaveToLoadedSource() {
    if (!pixelGrid) return;
    if (!pixelEditorLoadedSource || typeof pixelEditorLoadedSource !== 'string') return;

    if (pixelEditorLoadedSource.startsWith('retro:')) {
        const name = pixelEditorLoadedSource.slice('retro:'.length);
        const icon = pixelEncodeCellsToRetroIcon(pixelGrid.cells, pixelGrid.cols, pixelGrid.rows);
        if (!icon) return;
        if (typeof retroIcons === 'object' && retroIcons) {
            retroIcons[name] = icon;
        }
        pixelRefreshEditorSources();
        pixelUpdateUI();
        return;
    }

    if (pixelEditorLoadedSource.startsWith('preset:')) {
        const id = pixelEditorLoadedSource.slice('preset:'.length);
        const preset = pixelExportPreset();
        if (!preset) return;
        window.pixelPresets = window.pixelPresets || {};
        window.pixelPresets[id] = { v: 1, cols: preset.cols, rows: preset.rows, intensity: preset.intensity, cells: preset.cells };
        pixelRefreshEditorSources();
        pixelUpdateUI();
    }
}

function pixelAddNewSourceFromCurrent() {
    if (!pixelGrid) return;

    const kind = window.prompt('Add new as type: "retro" or "preset"?', 'preset');
    if (!kind) return;
    const k = String(kind).trim().toLowerCase();

    if (k === 'retro') {
        const suggested = 'new_icon_12x7';
        const name = window.prompt('New retroIcons key:', suggested);
        if (!name) return;
        const icon = pixelEncodeCellsToRetroIcon(pixelGrid.cells, pixelGrid.cols, pixelGrid.rows);
        if (!icon) return;
        if (typeof retroIcons === 'object' && retroIcons) {
            retroIcons[name] = icon;
            pixelEditorLoadedSource = `retro:${name}`;
        }
        pixelRefreshEditorSources();
        pixelUpdateUI();
        return;
    }

    if (k === 'preset') {
        const suggested = 'new_preset';
        const id = window.prompt('New preset id (for window.pixelPresets):', suggested);
        if (!id) return;
        const preset = pixelExportPreset();
        if (!preset) return;
        window.pixelPresets = window.pixelPresets || {};
        window.pixelPresets[id] = { v: 1, cols: preset.cols, rows: preset.rows, intensity: preset.intensity, cells: preset.cells };
        pixelEditorLoadedSource = `preset:${id}`;
        pixelRefreshEditorSources();
        pixelUpdateUI();
    }
}

function pixelRefreshEditorSources() {
    const sel = getEl('pixel-editor-source');
    if (!sel) return;

    const prevSelected = sel.value || '';

    const opts = [];
    const presets = window.pixelPresets || {};
    Object.keys(presets).sort().forEach((id) => {
        opts.push({ value: `preset:${id}`, label: `preset: ${id}` });
    });

    if (typeof retroIcons === 'object' && retroIcons) {
        Object.keys(retroIcons).sort().forEach((name) => {
            opts.push({ value: `retro:${name}`, label: `retro: ${name}` });
        });
    }

    sel.innerHTML = '';
    if (opts.length === 0) {
        const opt = document.createElement('option');
        opt.value = '';
        opt.textContent = '(no sources found)';
        sel.appendChild(opt);
        return;
    }

    opts.forEach((o) => {
        const opt = document.createElement('option');
        opt.value = o.value;
        opt.textContent = o.label;
        sel.appendChild(opt);
    });

    const preferred = (typeof pixelEditorLoadedSource === 'string' && pixelEditorLoadedSource)
        ? pixelEditorLoadedSource
        : prevSelected;
    if (preferred && opts.some(o => o.value === preferred)) {
        sel.value = preferred;
    }
}

function pixelLoadSelectedEditorSource() {
    if (!pixelGrid) return;
    const sel = getEl('pixel-editor-source');
    if (!sel) return;
    const v = sel.value || '';
    if (!v) return;

    if (v.startsWith('preset:')) {
        const id = v.slice('preset:'.length);
        const presets = window.pixelPresets || {};
        const preset = presets[id];
        if (preset) {
            pixelApplyPreset(preset);
            pixelEditorLoadedSource = v;
            pixelUpdateAllOverlays();
            pixelUpdateUI();
        }
        return;
    }

    if (v.startsWith('retro:')) {
        const name = v.slice('retro:'.length);
        const icon = (typeof retroIcons === 'object' && retroIcons) ? retroIcons[name] : null;
        if (icon) {
            const cells = pixelDecodeRetroIconToCells(icon, pixelGrid.cols, pixelGrid.rows, pixelActiveColorIndex);
            if (cells && cells.length === pixelGrid.cells.length) {
                pixelGrid.cells.set(cells);
                pixelEditorLoadedSource = v;
                pixelUpdateAllOverlays();
                pixelUpdateUI();
            }
        }
    }
}

function pixelDecodeRetroIconToCells(iconRows, cols, rows, onColorIndex) {
    if (!iconRows || !Array.isArray(iconRows)) return null;
    if (!cols || !rows) return null;
    const srcRows = iconRows.length;
    if (!srcRows) return null;
    const src = new Uint8Array(cols * srcRows);
    for (let row = 0; row < srcRows; row++) {
        const line = iconRows[row] || '';
        for (let col = 0; col < cols; col++) {
            const ch = line[col] || '·';
            const idx = col * srcRows + row;
            src[idx] = (ch === '█') ? (onColorIndex | 0) : 0;
        }
    }
    if (srcRows === rows) return src;
    return pixelRemapCells(src, cols, srcRows, cols, rows);
}

function pixelEncodeCellsToRetroIcon(cells, cols, rows) {
    if (!cells || !cols || !rows) return null;
    const out = [];
    for (let row = 0; row < rows; row++) {
        let line = '';
        for (let col = 0; col < cols; col++) {
            const idx = col * rows + row;
            const v = cells[idx] | 0;
            line += (v !== 0) ? '█' : '·';
        }
        out.push(line);
    }
    return out;
}

function pixelCopyText(text) {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
        return;
    }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        (domRootEl || document.body).appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
    } catch (e) {}
}

function pixelExportPreset() {
    if (!pixelGrid) return null;
    return {
        v: 1,
        cols: pixelGrid.cols,
        rows: pixelGrid.rows,
        intensity: pixelIntensityMode,
        palette: pixelPalette.map(c => (c ? c.replace('#', '').toLowerCase() : null)),
        cells: pixelEncodeCells(pixelGrid.cells)
    };
}

function effectPixelPresetShow(forcePresetId) {
    if (!pixelGrid) return null;

    const presets = window.pixelPresets || {};
    const presetIds = Object.keys(presets);
    if (presetIds.length === 0) return null;

    const matching = presetIds.filter((id) => {
        const p = presets[id];
        if (!p || p.v !== 1) return false;
        if (p.cols !== pixelGrid.cols) return false;
        return true;
    });
    const pool = (matching.length > 0) ? matching : presetIds;
    const pickedId = (forcePresetId && Object.prototype.hasOwnProperty.call(presets, forcePresetId))
        ? forcePresetId
        : pool[Math.floor(Math.random() * pool.length)];
    const picked = presets[pickedId];
    if (!picked) return null;

    const startTime = Date.now();
    const duration = 2600;

    const pushed = pixelPushTransientState();
    let applied = false;
    let restored = false;

    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;

            if (!applied) {
                pixelApplyPreset(picked);
                pixelIntensityMode = 'soft';
                pixelForceBinaryDisplay = true;
                pixelUpdateAllOverlays();
                applied = true;
            }

            if (progress >= 1 && !restored) {
                if (pushed) pixelPopTransientState();
                restored = true;
            }
        }
    };
}

const marqueeFont12x8 = {
  'A': [
    '000111111000',
    '001111111100',
    '001100001100',
    '001100001100',
    '001111111100',
    '001111111100',
    '001100001100',
    '001100001100',
  ],
  'B': [
    '001111111000',
    '001100011100',
    '001100001100',
    '001111111000',
    '001111111000',
    '001100001100',
    '001100011100',
    '001111111000',
  ],
  'C': [
    '000111111000',
    '001111111100',
    '001100000000',
    '001100000000',
    '001100000000',
    '001100000000',
    '001111111100',
    '000111111000',
  ],
  'D': [
    '001111110000',
    '001100111000',
    '001100011100',
    '001100001100',
    '001100001100',
    '001100011100',
    '001100111000',
    '001111110000',
  ],
  'E': [
    '001111111100',
    '001111111100',
    '001100000000',
    '001111110000',
    '001111110000',
    '001100000000',
    '001111111100',
    '001111111100',
  ],
  'F': [
    '001111111100',
    '001111111100',
    '001100000000',
    '001111110000',
    '001111110000',
    '001100000000',
    '001100000000',
    '001100000000',
  ],
  'G': [
    '000111111000',
    '001111111100',
    '001100000000',
    '001101111100',
    '001101111100',
    '001100001100',
    '001111111100',
    '000111111000',
  ],
  'H': [
    '001100001100',
    '001100001100',
    '001100001100',
    '001111111100',
    '001111111100',
    '001100001100',
    '001100001100',
    '001100001100',
  ],
  'I': [
    '000111111000',
    '000111111000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000111111000',
    '000111111000',
  ],
  'J': [
    '000000111100',
    '000000011000',
    '000000011000',
    '000000011000',
    '000000011000',
    '001100011000',
    '001111111000',
    '000111110000',
  ],
  'K': [
    '001100011100',
    '001100111000',
    '001101110000',
    '001111100000',
    '001111100000',
    '001101110000',
    '001100111000',
    '001100011100',
  ],
  'L': [
    '001100000000',
    '001100000000',
    '001100000000',
    '001100000000',
    '001100000000',
    '001100000000',
    '001111111100',
    '001111111100',
  ],
  'M': [
    '001100001100',
    '001110011100',
    '001111111100',
    '001101101100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
  ],
  'N': [
    '001100001100',
    '001110001100',
    '001111001100',
    '001101101100',
    '001100111100',
    '001100011100',
    '001100001100',
    '001100001100',
  ],
  'O': [
    '000111111000',
    '001111111100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001111111100',
    '000111111000',
  ],
  'P': [
    '001111111000',
    '001100011100',
    '001100001100',
    '001100001100',
    '001111111100',
    '001111111000',
    '001100000000',
    '001100000000',
  ],
  'Q': [
    '000111111000',
    '001111111100',
    '001100001100',
    '001100001100',
    '001100111100',
    '001111111100',
    '000111111110',
    '000000011110',
  ],
  'R': [
    '001111111000',
    '001100011100',
    '001100001100',
    '001111111000',
    '001101110000',
    '001101110000',
    '001100111000',
    '001100011100',
  ],
  'S': [
    '000111111000',
    '001111111100',
    '001100000000',
    '000111111000',
    '000111111000',
    '000000011100',
    '001111111100',
    '000111111000',
  ],
  'T': [
    '001111111100',
    '001111111100',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
  ],
  'U': [
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
    '001111111100',
    '000111111000',
  ],
  'V': [
    '001100001100',
    '001100001100',
    '001100001100',
    '001100001100',
    '000110011000',
    '000111111000',
    '000011110000',
    '000011110000',
  ],
  'W': [
    '001100001100',
    '001100001100',
    '001100001100',
    '001101101100',
    '001111111100',
    '001111111100',
    '001110011100',
    '001100001100',
  ],
  'X': [
    '001100001100',
    '001110011100',
    '000111111000',
    '000011110000',
    '000011110000',
    '000111111000',
    '001110011100',
    '001100001100',
  ],
  'Y': [
    '001100001100',
    '001110011100',
    '000111111000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
    '000011110000',
  ],
  'Z': [
    '001111111100',
    '001111111100',
    '000000111000',
    '000011110000',
    '000111000000',
    '001110000000',
    '001111111100',
    '001111111100',
  ],
  '0': [
    '000111111000',
    '001111111100',
    '001100011100',
    '001100111100',
    '001101101100',
    '001111001100',
    '001111111100',
    '000111111000',
  ],
  '1': [
    '000011110000',
    '000111110000',
    '001111110000',
    '000011110000',
    '000011110000',
    '000011110000',
    '001111111100',
    '001111111100',
  ],
  '2': [
    '000111111000',
    '001111111100',
    '000000011100',
    '000001111000',
    '000111100000',
    '001111000000',
    '001111111100',
    '001111111100',
  ],
  '3': [
    '000111111000',
    '001111111100',
    '000000011100',
    '000011111000',
    '000011111000',
    '000000011100',
    '001111111100',
    '000111111000',
  ],
  '4': [
    '001100011000',
    '001100011000',
    '001100011000',
    '001100011000',
    '001111111100',
    '001111111100',
    '000000011000',
    '000000011000',
  ],
  '5': [
    '001111111100',
    '001111111100',
    '001100000000',
    '001111111000',
    '001111111100',
    '000000011100',
    '001111111100',
    '000111111000',
  ],
  '6': [
    '000111111000',
    '001111111100',
    '001100000000',
    '001111111000',
    '001111111100',
    '001100011100',
    '001111111100',
    '000111111000',
  ],
  '7': [
    '001111111100',
    '001111111100',
    '000000111100',
    '000001111000',
    '000011110000',
    '000011100000',
    '000011100000',
    '000011100000',
  ],
  '8': [
    '000111111000',
    '001111111100',
    '001100001100',
    '000111111000',
    '000111111000',
    '001100001100',
    '001111111100',
    '000111111000',
  ],
  '9': [
    '000111111000',
    '001111111100',
    '001110001100',
    '001111111100',
    '000111111100',
    '000000001100',
    '001111111100',
    '000111111000',
  ],
  '.': [
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000001100000',
    '000001100000',
  ],
  ' ': [
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
    '000000000000',
  ],
};

function marqueeFontGet(char) {
    return marqueeFont12x8[char] || marqueeFont12x8[' '];
}

function marqueeRenderRows(text, height) {
    const s = String(text || '').toUpperCase();
    const h = Math.max(1, (height | 0) || 7);
    const rows = Array(h)
        .fill(0)
        .map(() => []);

    const interLetterGap = 1;
    const spaceGap = 3;

    const cropGlyph = (glyph) => {
        if (!glyph || !glyph.length) return glyph;
        const w = glyph[0] ? glyph[0].length : 0;
        if (!w) return glyph;
        let min = w;
        let max = -1;
        for (let r = 0; r < glyph.length; r++) {
            const line = glyph[r] || '';
            for (let c = 0; c < line.length; c++) {
                if (line[c] === '1') {
                    if (c < min) min = c;
                    if (c > max) max = c;
                }
            }
        }
        if (max < 0 || min >= w) return glyph;
        return glyph.map((line) => (line || '').slice(min, max + 1));
    };

    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === ' ') {
            for (let r = 0; r < h; r++) {
                for (let c = 0; c < spaceGap; c++) rows[r].push(false);
            }
            continue;
        }

        const glyph = cropGlyph(marqueeFontGet(ch));
        const glyphWidth = (glyph && glyph[0]) ? glyph[0].length : 12;
        const emptyLine = '0'.repeat(glyphWidth);
        for (let r = 0; r < h; r++) {
            const line = glyph[r] || emptyLine;
            for (let c = 0; c < line.length; c++) rows[r].push(line[c] === '1');
            // 1 column spacing
            for (let c = 0; c < interLetterGap; c++) rows[r].push(false);
        }
    }
    return rows;
}

function effectPixelMarquee(message) {
    if (!pixelGrid) return null;

    const gridCols = gridDimensions.cols || 12;
    const gridRows = pixelGrid.rows || gridDimensions.rows || 7;

    const messages = ['OBSOLETE', '2029', 'WORK THE FUTURE', 'AUTHENTIC', 'PROTECT', 'A.I.', 'HUMAN'];
    const msg = message ? String(message) : messages[Math.floor(Math.random() * messages.length)];
    const msgRows = marqueeRenderRows(msg, gridRows);
    const msgWidth = msgRows[0] ? msgRows[0].length : 0;

    // padding: blank screen on both sides so text enters/exits cleanly
    const totalCols = gridCols + msgWidth + gridCols;
    const startCol = gridCols;

    const full = Array(gridRows)
        .fill(0)
        .map(() => Array(totalCols).fill(false));
    for (let r = 0; r < gridRows; r++) {
        const row = msgRows[r] || [];
        for (let c = 0; c < row.length; c++) {
            full[r][startCol + c] = !!row[c];
        }
    }

    const frames = Math.max(1, totalCols - gridCols);
    const duration = Math.min(6500, Math.max(2800, frames * 75));
    const startTime = Date.now();

    const pushed = pixelPushTransientState();
    let restored = false;

    const panelData = [];
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                panelData.push({ panel });
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }

    const colorIndex = 14;

    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = Math.min(1, Math.max(0, elapsed / duration));

            // flip-in/out window
            const flipInEnd = 0.16;
            const flipOutStart = 0.84;

            panelData.forEach(data => {
                if (progress < flipInEnd) {
                    const p = progress / flipInEnd;
                    data.panel.rotation.y = Math.PI * p;
                } else if (progress > flipOutStart) {
                    const p = (progress - flipOutStart) / (1 - flipOutStart);
                    data.panel.rotation.y = Math.PI + (Math.PI * p);
                } else {
                    data.panel.rotation.y = Math.PI;
                }
            });

            // scrolling while flipped
            if (progress >= flipInEnd && progress <= flipOutStart) {
                const scrollProgress = (progress - flipInEnd) / (flipOutStart - flipInEnd);
                const offset = Math.floor(scrollProgress * frames);
                const cells = new Uint8Array(gridCols * gridRows);

                for (let col = 0; col < gridCols; col++) {
                    for (let row = 0; row < gridRows; row++) {
                        const bit = full[row] && full[row][offset + col];
                        const idx = col * gridRows + row;
                        cells[idx] = bit ? colorIndex : 0;
                    }
                }

                pixelGrid.cells.set(cells);
                pixelForceBinaryDisplay = true;
                pixelUpdateAllOverlays();
            }

            if (progress >= 1) {
                if (!restored) {
                    if (pushed) pixelPopTransientState();
                    restored = true;
                }
                panelData.forEach(data => {
                    data.panel.rotation.y = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

function pixelDownloadJSON() {
    const preset = pixelExportPreset();
    if (!preset) return;
    const blob = new Blob([JSON.stringify(preset, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pixel-grid-v1.json';
    (domRootEl || document.body).appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 250);
}

function pixelCopyPxUrl() {
    const px = pixelEncodePxPayload();
    if (!px) return;
    const url = new URL(window.location.href);
    url.searchParams.set('px', px);
    url.searchParams.delete('pxid');
    const text = url.toString();
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
    }
}

function pixelEncodeCells(cells) {
    const packed = new Uint8Array(Math.ceil(cells.length / 2));
    for (let i = 0; i < cells.length; i += 2) {
        const hi = cells[i] & 0x0f;
        const lo = (i + 1 < cells.length) ? (cells[i + 1] & 0x0f) : 0;
        packed[i / 2] = (hi << 4) | lo;
    }
    return pixelBytesToBase64Url(packed);
}

function pixelDecodeCells(encoded, expectedLen) {
    const packed = pixelBase64UrlToBytes(encoded);
    if (!packed) return null;
    const expectedPackedLen = Math.ceil(expectedLen / 2);
    if (packed.length !== expectedPackedLen) return null;
    const out = new Uint8Array(expectedLen);
    for (let i = 0; i < expectedLen; i++) {
        const b = packed[Math.floor(i / 2)] || 0;
        out[i] = (i % 2 === 0) ? ((b >> 4) & 0x0f) : (b & 0x0f);
    }
    return out;
}

function pixelDecodeHexCells(encoded, expectedLen) {
    if (typeof encoded !== 'string') return null;
    const s = encoded.trim();
    if (!s) return null;
    if (s.length !== expectedLen * 2) return null;
    const out = new Uint8Array(expectedLen);
    for (let i = 0; i < expectedLen; i++) {
        const pair = s.slice(i * 2, i * 2 + 2);
        const v = Number.parseInt(pair, 16);
        if (!Number.isFinite(v)) return null;
        out[i] = v & 0x0f;
    }
    return out;
}

function pixelEncodePxPayload() {
    if (!pixelGrid) return null;
    const encoded = pixelEncodeCells(pixelGrid.cells);
    return `V2.${pixelGrid.cols}x${pixelGrid.rows}.${encoded}`;
}

function pixelDecodePxPayload(px) {
    if (!pixelGrid) return null;
    if (typeof px !== 'string') return null;
    const parts = px.split('.');

    // V2: includes explicit dimensions, supports remapping when grid dims change.
    // Format: V2.<cols>x<rows>.<base64url>
    if (parts[0] === 'V2' && parts.length === 3) {
        const dim = parts[1];
        const match = dim.match(/^(\d+)x(\d+)$/);
        if (!match) return null;
        const srcCols = Number(match[1]);
        const srcRows = Number(match[2]);
        if (!Number.isFinite(srcCols) || !Number.isFinite(srcRows) || srcCols <= 0 || srcRows <= 0) return null;

        const srcLen = srcCols * srcRows;
        const decodedCells = pixelDecodeCells(parts[2], srcLen);
        if (!decodedCells) return null;

        const remapped = pixelRemapCells(decodedCells, srcCols, srcRows, pixelGrid.cols, pixelGrid.rows);
        if (!remapped) return null;

        return {
            v: 1,
            cols: pixelGrid.cols,
            rows: pixelGrid.rows,
            intensity: 'soft',
            cells: remapped
        };
    }

    // V1 legacy: assumes current grid dims.
    // Format: V1.<base64url>
    if (parts[0] === 'V1' && parts.length === 2) {
        const decodedCells = pixelDecodeCells(parts[1], pixelGrid.cells.length);
        if (!decodedCells) return null;
        return {
            v: 1,
            cols: pixelGrid.cols,
            rows: pixelGrid.rows,
            intensity: 'soft',
            cells: decodedCells
        };
    }

    return null;
}

function pixelRemapCells(srcCells, srcCols, srcRows, dstCols, dstRows) {
    if (!srcCells) return null;
    if (srcCols !== dstCols) return null;

    // Exact match
    if (srcRows === dstRows) {
        if (srcCells.length !== dstCols * dstRows) return null;
        return srcCells;
    }

    // Future-proofing: when you insert an explainer row before CTA.
    // Current structure (per sector column):
    // 0 title
    // 1-5 scenarios
    // 6 CTA
    // New 8-row structure (proposed):
    // 0 title
    // 1-5 scenarios
    // 6 explainer (new)
    // 7 CTA
    if (srcRows === 7 && dstRows === 8) {
        const out = new Uint8Array(dstCols * dstRows);
        for (let col = 0; col < dstCols; col++) {
            for (let srcRow = 0; srcRow < srcRows; srcRow++) {
                const srcIdx = col * srcRows + srcRow;
                const v = srcCells[srcIdx] | 0;
                let dstRow = srcRow;
                if (srcRow === 6) dstRow = 7; // shift CTA down
                const dstIdx = col * dstRows + dstRow;
                out[dstIdx] = v;
            }
            // explainer row (6) stays 0
        }
        return out;
    }

    if (srcRows === 8 && dstRows === 7) {
        const out = new Uint8Array(dstCols * dstRows);
        for (let col = 0; col < dstCols; col++) {
            for (let dstRow = 0; dstRow < dstRows; dstRow++) {
                const srcIdx = col * srcRows + dstRow;
                const dstIdx = col * dstRows + dstRow;
                out[dstIdx] = srcCells[srcIdx] | 0;
            }
        }
        return out;
    }

    return null;
}

function pixelBytesToBase64Url(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function pixelBase64UrlToBytes(s) {
    try {
        const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
        const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
        const bin = atob(b64 + pad);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    } catch (e) {
        return null;
    }
}

// Extract node IDs from logic field
function extractNodes(logicText) {
    if (!logicText) return [];
    const nodePattern = /([a-z_]+_[a-z_0-9]+)/gi;
    const matches = logicText.match(nodePattern) || [];
    return [...new Set(matches)]; // Remove duplicates
}

// Add corner rivets to panel
function addCornerRivets(panel, width, height) {
    const rivetGeometry = new THREE.CircleGeometry(0.08, 8);
    const rivetMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFF00 });
    
    const corners = [
        [-width/2 + 0.2, height/2 - 0.2],
        [width/2 - 0.2, height/2 - 0.2],
        [-width/2 + 0.2, -height/2 + 0.2],
        [width/2 - 0.2, -height/2 + 0.2]
    ];
    
    corners.forEach(([x, y]) => {
        const rivet = new THREE.Mesh(rivetGeometry, rivetMaterial);
        rivet.position.set(x, y, 0.01);
        panel.add(rivet);
    });
}

// Add panel number label
function addPanelNumber(panel, number, width, height) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 128;
    canvas.height = 64;
    
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.fillStyle = '#FFFF00';
    context.font = '900 32px Arial Black';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(number).padStart(2, '0'), canvas.width / 2, canvas.height / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    
    sprite.position.set(-width/2 + 0.4, -height/2 + 0.3, 0.01);
    sprite.scale.set(0.5, 0.25, 1);
    panel.add(sprite);
}

function createGridLines(cols, rows, spacing) {
    if (gridLinesGroup) {
        scene.remove(gridLinesGroup);
        gridLinesGroup = null;
    }

    gridLinesGroup = new THREE.Group();

    const material = new THREE.LineBasicMaterial({ color: 0xFFFF00, opacity: 0.15, transparent: true });
    const width = (cols - 1) * spacing;
    const height = (rows - 1) * spacing;
    
    // Horizontal lines (thicker, more industrial)
    for (let i = 0; i <= rows; i++) {
        const y = height / 2 - i * spacing;
        const hGeo = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-width / 2 - 2, y, -2),
            new THREE.Vector3(width / 2 + 2, y, -2)
        ]);
        gridLinesGroup.add(new THREE.Line(hGeo, material));
    }
    
    // Vertical lines
    for (let i = 0; i <= cols; i++) {
        const x = -width / 2 + i * spacing;
        const vGeo = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(x, -height / 2 - 2, -2),
            new THREE.Vector3(x, height / 2 + 2, -2)
        ]);
        gridLinesGroup.add(new THREE.Line(vGeo, material));
    }

    scene.add(gridLinesGroup);
}

function createParticles() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(200 * 3);
    for (let i = 0; i < 600; i++) {
        positions[i] = (Math.random() - 0.5) * 100;
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({ color: 0xFFFF00, size: 0.1, transparent: true, opacity: 0.6 });
    scene.add(new THREE.Points(geometry, material));
}

// Create 3D text labels for sectors
function createSectorLabels(sectorColumns, labelY) {
    sectorLabelSprites.forEach(sprite => scene.remove(sprite));
    sectorLabelSprites = [];

    Object.entries(sectorColumns).forEach(([sector, data]) => {
        // Create new canvas for each sector
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 512;
        canvas.height = 128;
        
        // Draw stencil-style text
        context.fillStyle = data.color || '#FFFF00';
        context.font = '900 70px Arial Black';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(sector, canvas.width / 2, canvas.height / 2);
        
        // Add outline for stencil effect
        context.strokeStyle = '#000000';
        context.lineWidth = 3;
        context.strokeText(sector, canvas.width / 2, canvas.height / 2);
        
        // Create texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({ 
            map: texture, 
            transparent: true,
            opacity: 0.9
        });
        const sprite = new THREE.Sprite(material);
        
        // Position above the column (higher to avoid overlap)
        sprite.position.set(data.x, labelY, 0);
        sprite.scale.set(4, 1, 1);
        sprite.userData.isSectorLabel = true;
        sprite.userData.sector = sector;
        
        scene.add(sprite);
        sectorLabelSprites.push(sprite);
    });
}

// Create visual connections between panels that share nodes
function createNodeConnections() {
    const nodeMap = {}; // Map of node -> [panel indices]
    
    // Build node map
    panels.forEach((panel, idx) => {
        if (panel.userData.nodes) {
            panel.userData.nodes.forEach(node => {
                if (!nodeMap[node]) nodeMap[node] = [];
                nodeMap[node].push(idx);
            });
        }
    });
    
    // Create connections for nodes shared by multiple panels
    Object.entries(nodeMap).forEach(([node, panelIndices]) => {
        if (panelIndices.length > 1) {
            // Connect all panels that share this node
            for (let i = 0; i < panelIndices.length - 1; i++) {
                for (let j = i + 1; j < panelIndices.length; j++) {
                    const panel1 = panels[panelIndices[i]];
                    const panel2 = panels[panelIndices[j]];
                    
                    // Create line between panels
                    const points = [
                        new THREE.Vector3(
                            panel1.userData.originalPosition.x,
                            panel1.userData.originalPosition.y,
                            panel1.userData.originalPosition.z - 1
                        ),
                        new THREE.Vector3(
                            panel2.userData.originalPosition.x,
                            panel2.userData.originalPosition.y,
                            panel2.userData.originalPosition.z - 1
                        )
                    ];
                    
                    const geometry = new THREE.BufferGeometry().setFromPoints(points);
                    const material = new THREE.LineBasicMaterial({
                        color: 0xFFFF00,
                        transparent: true,
                        opacity: 0.2,
                        linewidth: 2
                    });
                    const line = new THREE.Line(geometry, material);
                    line.userData.isNodeConnection = true;
                    line.userData.node = node;
                    line.userData.panels = [panelIndices[i], panelIndices[j]];
                    
                    scene.add(line);
                }
            }
        }
    });
    
    console.log('Created node connections for', Object.keys(nodeMap).length, 'unique nodes');
}

function updateLoadingProgress() {
    const progress = expectedSlides > 0 ? (loadedImages / expectedSlides) * 100 : 0;
    const bar = getEl('loading-progress');
    if (bar) bar.style.width = progress + '%';
    if (expectedSlides > 0 && loadedImages >= expectedSlides) {
        setTimeout(() => {
            const loading = getEl('loading');
            if (loading) loading.classList.add('hidden');
        }, 500);
    }
}

function onMouseMove(event) {
    updateMouseFromEvent(event);

    if (!isDetailView && isPixelEditMode) {
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(panels, true);
        const panel = resolvePanelFromIntersects(intersects);
        if (panel) {
            (domRootEl || document.body).style.cursor = 'cell';
            if (pixelIsPainting) {
                const key = `${panel.userData.sectorColumn}|${panel.userData.rowInSector}|${pixelIsErasing ? 1 : 0}|${pixelActiveColorIndex}`;
                if (pixelLastPaintKey !== key) {
                    pixelLastPaintKey = key;
                    pixelSuppressClick = true;
                    pixelSetCell(panel, pixelIsErasing ? 0 : pixelActiveColorIndex);
                }
            }
        } else {
            (domRootEl || document.body).style.cursor = 'crosshair';
        }
        return;
    }

    if (!isDetailView) {
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(panels, true);
        
        // Reset previous hover
        if (hoveredPanel && hoveredPanel.material.uniforms) {
            hoveredPanel.material.uniforms.glitchIntensity.value = 0;
        }
        
        panels.forEach(p => {
            if (p.children[0]) p.children[0].material.color.setHex(parseInt(p.userData.color.replace('#', '0x')));
        });
        
        const panel = resolvePanelFromIntersects(intersects);
        if (panel) {
            hoveredPanel = panel;
            if (panel.children[0]) panel.children[0].material.color.setHex(0xFFFFFF);
            
            // Trigger glitch effect on hover
            if (panel.material.uniforms) {
                panel.material.uniforms.glitchIntensity.value = 0.3;
            }
            
            (domRootEl || document.body).style.cursor = 'pointer';
        } else {
            hoveredPanel = null;
            (domRootEl || document.body).style.cursor = 'crosshair';
        }
    } else {
        // In detail view, check for text panel hover with debouncing
        if (isTransitioning) return; // Skip if currently transitioning
        
        raycaster.setFromCamera(mouse, camera);
        const textIntersects = raycaster.intersectObjects(textPanels);
        
        // Clear existing debounce timer
        if (hoverDebounceTimer) {
            clearTimeout(hoverDebounceTimer);
        }
        
        if (textIntersects.length > 0) {
            const panel = textIntersects[0].object;
            
            // Debounce hover changes
            hoverDebounceTimer = setTimeout(() => {
                // New hover - zoom in
                if (hoveredTextPanel !== panel && !isTransitioning) {
                    isTransitioning = true;
                    
                    // Reset previous panel if exists
                    if (hoveredTextPanel) {
                        resetTextPanelHover(hoveredTextPanel);
                    }
                    
                    // Store original state from current position
                    textPanelOriginalState = {
                        position: { x: panel.position.x, y: panel.position.y, z: panel.position.z },
                        scale: { x: panel.scale.x, y: panel.scale.y, z: panel.scale.z },
                        baseX: panel.userData.baseX,
                        baseY: panel.userData.baseY,
                        baseZ: panel.userData.baseZ
                    };
                    
                    hoveredTextPanel = panel;
                    
                    // Stop floating immediately
                    panel.userData.isHovered = true;
                    
                    // Zoom in and center - move to screen center
                    const targetScale = 2.0; // 100% larger (2x)
                    animateScale(panel, { x: targetScale, y: targetScale, z: 1 }, 400);
                    animatePosition(panel, { x: 0, y: 0, z: 10 }, 400); // Move to screen center, forward
                    
                    // Unlock after transition
                    setTimeout(() => {
                        isTransitioning = false;
                    }, 450);
                }
            }, 100); // 100ms debounce
            
            (domRootEl || document.body).style.cursor = 'pointer';
        } else {
            // Mouse left text panels - debounce the reset too
            hoverDebounceTimer = setTimeout(() => {
                if (hoveredTextPanel && !isTransitioning) {
                    isTransitioning = true;
                    resetTextPanelHover(hoveredTextPanel);
                    hoveredTextPanel = null;
                    textPanelOriginalState = null;
                    
                    // Unlock after transition
                    setTimeout(() => {
                        isTransitioning = false;
                    }, 450);
                }
            }, 150); // Slightly longer debounce for mouse out
            
            (domRootEl || document.body).style.cursor = 'default';
        }
    }
}

// Reset text panel to original state after hover
function resetTextPanelHover(panel) {
    if (!textPanelOriginalState || !panel) return;
    
    // Stop floating immediately
    panel.userData.isHovered = false;
    
    // Restore original state
    animatePosition(panel, textPanelOriginalState.position, 400);
    animateScale(panel, textPanelOriginalState.scale, 400);
    
    // Wait for animation to complete before restoring base positions
    setTimeout(() => {
        if (panel.userData) {
            panel.userData.baseX = textPanelOriginalState.baseX;
            panel.userData.baseY = textPanelOriginalState.baseY;
            panel.userData.baseZ = textPanelOriginalState.baseZ;
        }
    }, 400);
}

function onMouseClick(event) {
    updateMouseFromEvent(event);
    if (!isDetailView && isPixelEditMode) {
        if (pixelSuppressClick) {
            pixelSuppressClick = false;
            return;
        }
        if (event && event.detail >= 2) {
            return;
        }
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(panels, true);
        const panel = resolvePanelFromIntersects(intersects);
        if (panel) {
            const idx = pixelCellIndexForPanel(panel);
            if (idx !== null) {
                const next = (event && event.shiftKey) ? 0 : pixelActiveColorIndex;
                pixelSetCell(panel, next);
            }
        }
        return;
    }
    raycaster.setFromCamera(mouse, camera);
    const labelIntersects = !isDetailView ? raycaster.intersectObjects(sectorLabelSprites) : [];

    if (!isDetailView && labelIntersects.length > 0) {
        const sector = labelIntersects[0].object.userData.sector;
        const sectorLanding = panels
            .filter(p => p.userData && p.userData.sector === sector)
            .sort((a, b) => a.userData.rowInSector - b.userData.rowInSector)[0];
        if (sectorLanding) {
            selectPanel(sectorLanding);
        }
        return;
    }

    const intersects = raycaster.intersectObjects(panels, true);
    
    if (isDetailView) {
        // In detail view - check if clicking a thumbnail
        let clickedPanel = null;
        for (let i = 0; i < intersects.length; i++) {
            const hit = intersects[i] && intersects[i].object;
            const resolved = resolvePanelFromObject(hit);
            if (!resolved) continue;
            if (resolved !== selectedPanel && sectorPanels.includes(resolved)) {
                clickedPanel = resolved;
                break;
            }
        }

        if (clickedPanel) {
            switchToPanel(clickedPanel);
            return;
        }

        const fallback = resolvePanelFromIntersects(intersects);
        if (fallback) {
            // Clicked main panel or any other panel - return to grid
            returnToGrid();
        } else {
            // Clicked background - return to grid
            returnToGrid();
        }
    } else {
        // In grid view - select panel if clicked
        const candidate = resolvePanelFromIntersects(intersects);
        if (candidate) {
            selectPanel(candidate);
        }
    }
}

let sectorPanels = []; // Panels in the selected sector

function isTabletLandscapeDetail() {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    const coarseLandscape = window.matchMedia('(hover: none) and (pointer: coarse) and (orientation: landscape)').matches;
    const widthLandscape = window.matchMedia('(max-width: 1024px) and (orientation: landscape)').matches;
    return coarseLandscape || widthLandscape;
}

function detailLayoutX(z, mainScale, thumbScale) {
     const zz = (typeof z === 'number' && isFinite(z) && z > 0) ? z : (camera && camera.position ? camera.position.z : 30);
     const aspect = (window.innerWidth && window.innerHeight) ? (window.innerWidth / window.innerHeight) : 1;
     const fov = (camera && camera.fov) ? camera.fov : 60;
     const halfW = Math.tan((fov * Math.PI / 180) / 2) * zz * aspect;

     const sidebarEl = document && document.querySelector ? document.querySelector('.dashboard-sidebar') : null;
     const sidebarRect = sidebarEl && sidebarEl.getBoundingClientRect ? sidebarEl.getBoundingClientRect() : null;
     const sidebarPx = sidebarRect ? (sidebarRect.width || 0) : 450;
     const sidebarLeftPx = sidebarRect ? (sidebarRect.left || 0) : 0;
     const worldPerPx = (window.innerWidth ? ((halfW * 2) / window.innerWidth) : 0);
     const sidebarWorld = sidebarPx * worldPerPx;

     const panelHalf = (panelWidth * mainScale) / 2;
     const margin = 1.5;

     const usableLeft = -halfW;
     const usableRight = (sidebarRect && isFinite(sidebarLeftPx) && sidebarLeftPx > 0)
         ? (usableLeft + (sidebarLeftPx * worldPerPx))
         : (halfW - sidebarWorld);

     const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
     const panelMinX = usableLeft + panelHalf + margin;
     const panelMaxX = usableRight - panelHalf - margin;

     let panelX = clamp((usableLeft + usableRight) / 2, panelMinX, panelMaxX);

     const thumbHalf = (panelWidth * thumbScale) / 2;
     const thumbMinX = usableLeft + thumbHalf + margin;

     const computeThumbX = (panelLeft) => {
         const thumbMaxX = panelLeft - thumbHalf - margin;
         return clamp((usableLeft + panelLeft) / 2, thumbMinX, thumbMaxX);
     };

     let panelLeft = panelX - panelHalf;
     let thumbX = computeThumbX(panelLeft);

     const groupLeft = Math.min(panelLeft, thumbX - thumbHalf);
     const groupRight = panelX + panelHalf;
     const groupCenter = (groupLeft + groupRight) / 2;
     const targetCenter = (usableLeft + usableRight) / 2;

     panelX = clamp(panelX + (targetCenter - groupCenter), panelMinX, panelMaxX);
     panelLeft = panelX - panelHalf;
     thumbX = computeThumbX(panelLeft);

     return { panelX, thumbX, usableLeft, usableRight };
}

function selectPanel(panel) {
    if (!panel || !panel.userData || !panel.userData.item) return;
    // Ensure any previous video is cleaned up
    if (selectedPanel && selectedPanel !== panel) {
        detachVideoTextureFromPanel(selectedPanel);
    }
    selectedPanel = panel;
    isDetailView = true;
    setDetailFlag(true);

    resetPanelsForDetailView();
    
    // Stop slogans when entering detail view
    if (stopSlogansCallback) {
        stopSlogansCallback();
    } else if (window.stopSlogans) {
        window.stopSlogans();
    }
    
    // Trigger strong glitch effect on selection
    if (panel.material.uniforms) {
        panel.material.uniforms.glitchIntensity.value = 0;
        panel.material.uniforms.rgbShift.value = 0;
    }
    
    const selectedColumn = panel.userData.sectorColumn;
    const selectedSector = panel.userData.sector;
    
    // Get all panels in the selected sector
    sectorPanels = panels.filter(p => p.userData.sector === selectedSector);

    applySectorLock(selectedSector);
    
    // Move camera closer for detail view
    animateCamera(camera.position, new THREE.Vector3(0, 0, 30), 600);
    
    // Slide columns away
    panels.forEach(p => {
        const panelColumn = p.userData.sectorColumn;
        
        if (panelColumn < selectedColumn) {
            // Columns to the left - slide further left
            const slideDistance = -60;
            animatePosition(p, { 
                x: p.userData.originalPosition.x + slideDistance, 
                y: p.userData.originalPosition.y, 
                z: p.userData.originalPosition.z 
            }, 800);
            animateOpacity(p.material, 0, 800);
        } else if (panelColumn > selectedColumn) {
            // Columns to the right - slide further right
            const slideDistance = 60;
            animatePosition(p, { 
                x: p.userData.originalPosition.x + slideDistance, 
                y: p.userData.originalPosition.y, 
                z: p.userData.originalPosition.z 
            }, 800);
            animateOpacity(p.material, 0, 800);
        }
    });
    
    // Scale and position selected panel large
    setTimeout(() => {
        const isTabletLayout = isTabletLandscapeDetail();
        const mainScale = isTabletLayout ? 5 : 7;
        const thumbScale = 0.7;
        const { panelX, thumbX, usableLeft, usableRight } = detailLayoutX(30, mainScale, thumbScale);
        const panelY = isTabletLayout ? 1.5 : 0;

        animatePosition(panel, { x: panelX, y: panelY, z: 0 }, 600);
        animateScale(panel, { x: mainScale, y: mainScale, z: 1 }, 600);

        // Detail-only: swap to video (mp4) if available
        attachVideoTextureToPanel(panel);

        const otherSectorPanels = sectorPanels.filter(p => p !== panel);
        if (isTabletLayout) {
            const thumbSpacing = 0.8;
            const thumbWidth = panelWidth * thumbScale;
            const availableWidth = usableRight - usableLeft;
            const maxThumbsByWidth = Math.max(1, Math.floor((availableWidth + thumbSpacing) / (thumbWidth + thumbSpacing)));
            const maxThumbs = Math.min(otherSectorPanels.length, 6, maxThumbsByWidth);
            const visibleThumbs = otherSectorPanels.slice(0, maxThumbs);
            const rowWidth = visibleThumbs.length
                ? (visibleThumbs.length * thumbWidth + (visibleThumbs.length - 1) * thumbSpacing)
                : 0;
            const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
            const rowLeft = clamp((usableLeft + usableRight) / 2 - rowWidth / 2, usableLeft, usableRight - rowWidth);
            const panelHalfHeight = (panelHeight * mainScale) / 2;
            const thumbHalfHeight = (panelHeight * thumbScale) / 2;
            const rowY = (panelY - panelHalfHeight) - thumbHalfHeight - 0.8;

            visibleThumbs.forEach((p, idx) => {
                const thumbXPos = rowLeft + idx * (thumbWidth + thumbSpacing) + thumbWidth / 2;
                animatePosition(p, { x: thumbXPos, y: rowY, z: 5 }, 600);
                animateScale(p, { x: thumbScale, y: thumbScale, z: 1 }, 600);
                animateOpacity(p.material, 1, 600);
            });
        } else {
            // Arrange other sector panels as vertical thumbnails on LEFT MARGIN
            const thumbSpacing = 3.0;
            const maxThumbs = Math.min(otherSectorPanels.length, 6);
            const visibleThumbs = otherSectorPanels.slice(0, maxThumbs);
            const thumbStartY = (visibleThumbs.length - 1) * thumbSpacing / 2;

            visibleThumbs.forEach((p, idx) => {
                const thumbY = thumbStartY - idx * thumbSpacing;
                animatePosition(p, { x: thumbX, y: thumbY, z: 5 }, 600);
                animateScale(p, { x: thumbScale, y: thumbScale, z: 1 }, 600);
                animateOpacity(p.material, 1, 600);
            });
        }

        fadeSectorLabels(selectedSector);
    }, 400);

    showHUD(panel.userData.item, panel.userData.index);
}

function switchToPanel(newPanel) {
    const oldPanel = selectedPanel;

    if (oldPanel && oldPanel !== newPanel) {
        detachVideoTextureFromPanel(oldPanel);
    }

    // Mark the new panel as selected immediately so detail-only video logic can attach correctly
    selectedPanel = newPanel;
    
    // Subtle camera movement - keep zoomed out enough to see everything
    const cameraOffset = {
        x: (Math.random() - 0.5) * 0.5, // Subtle horizontal shift
        y: (Math.random() - 0.5) * 0.3, // Subtle vertical shift
        z: 30 + (Math.random() - 0.5) * 1 // Stay zoomed out (30 units)
    };
    animateCamera(camera.position, new THREE.Vector3(cameraOffset.x, cameraOffset.y, cameraOffset.z), 600);
    
    // Slide old panel out with controlled movement
    if (oldPanel) {
        const exitDirection = Math.random() > 0.5 ? 1 : -1;
        animatePosition(oldPanel, { 
            x: oldPanel.position.x + exitDirection * 5, // Reduced from 15
            y: oldPanel.position.y + (Math.random() - 0.5) * 2, // Reduced from 5
            z: -2 // Reduced from -5
        }, 300);
        animateScale(oldPanel, { x: 0.8, y: 0.8, z: 1 }, 300);
        animateOpacity(oldPanel.material, 0.3, 300);
    }
    
    // Bring new panel in with controlled entrance animation
    setTimeout(() => {
        const isTabletLayout = isTabletLandscapeDetail();
        const mainScale = isTabletLayout ? 5 : 7;
        const thumbScale = 0.7;
        const { panelX, thumbX, usableLeft, usableRight } = detailLayoutX(30, mainScale, thumbScale);
        const panelY = isTabletLayout ? 1.5 : 0;

        // Start from subtle offset position
        const entryDirection = Math.random() > 0.5 ? 1 : -1;
        newPanel.position.set(panelX + entryDirection * 4, (Math.random() - 0.5) * 1.5, -1.5);
        // Slide new panel in
        animatePosition(newPanel, { x: panelX, y: panelY, z: 0 }, 400);
        animateScale(newPanel, { x: mainScale, y: mainScale, z: 1 }, 400);
        animateOpacity(newPanel.material, 1, 400);

        // Detail-only: swap to video (mp4) if available
        attachVideoTextureToPanel(newPanel);

        // Update thumbnail positions with smooth transition
        const otherSectorPanels = sectorPanels.filter(p => p !== newPanel);
        if (isTabletLayout) {
            const thumbSpacing = 0.8;
            const thumbWidth = panelWidth * thumbScale;
            const availableWidth = usableRight - usableLeft;
            const maxThumbsByWidth = Math.max(1, Math.floor((availableWidth + thumbSpacing) / (thumbWidth + thumbSpacing)));
            const maxThumbs = Math.min(otherSectorPanels.length, 6, maxThumbsByWidth);
            const visibleThumbs = otherSectorPanels.slice(0, maxThumbs);
            const rowWidth = visibleThumbs.length
                ? (visibleThumbs.length * thumbWidth + (visibleThumbs.length - 1) * thumbSpacing)
                : 0;
            const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
            const rowLeft = clamp((usableLeft + usableRight) / 2 - rowWidth / 2, usableLeft, usableRight - rowWidth);
            const panelHalfHeight = (panelHeight * mainScale) / 2;
            const thumbHalfHeight = (panelHeight * thumbScale) / 2;
            const rowY = (panelY - panelHalfHeight) - thumbHalfHeight - 0.8;

            visibleThumbs.forEach((p, idx) => {
                const delay = idx * 30;
                setTimeout(() => {
                    const thumbXPos = rowLeft + idx * (thumbWidth + thumbSpacing) + thumbWidth / 2;
                    animatePosition(p, { x: thumbXPos, y: rowY, z: 5 }, 400);
                    animateScale(p, { x: thumbScale, y: thumbScale, z: 1 }, 400);
                    animateOpacity(p.material, 1, 400);
                }, delay);
            });
        } else {
            const thumbSpacing = 3.0;
            const maxThumbs = Math.min(otherSectorPanels.length, 6);
            const visibleThumbs = otherSectorPanels.slice(0, maxThumbs);
            const thumbStartY = (visibleThumbs.length - 1) * thumbSpacing / 2;

            visibleThumbs.forEach((p, idx) => {
                const delay = idx * 30;
                setTimeout(() => {
                    const thumbY = thumbStartY - idx * thumbSpacing;
                    animatePosition(p, { x: thumbX, y: thumbY, z: 5 }, 400);
                    animateScale(p, { x: thumbScale, y: thumbScale, z: 1 }, 400);
                    animateOpacity(p.material, 1, 400);
                }, delay);
            });
        }
    }, 200);
    

    showHUD(newPanel.userData.item, newPanel.userData.index);
}

function returnToGrid() {
    // Ensure any active videos are stopped and textures restored
    try {
        activeVideoPanels.forEach((p) => detachVideoTextureFromPanel(p));
        activeVideoPanels = new Set();
    } catch (e) {
    }

    resetPanelsForGridView();
    
    // Set flag to prevent base animations from interfering
    isReturningToGrid = true;
    
    isDetailView = false;
    setDetailFlag(false);

    if (detailPrevPixelIntensityMode !== null) {
        pixelIntensityMode = detailPrevPixelIntensityMode;
        detailPrevPixelIntensityMode = null;
    }
    if (detailPrevForceBinaryDisplay !== null) {
        pixelForceBinaryDisplay = detailPrevForceBinaryDisplay;
        detailPrevForceBinaryDisplay = null;
    }
    if (pixelGrid) {
        pixelUpdateAllOverlays();
    }
    
    // Restart slogans when returning to grid view
    if (!motionDisabled) {
        if (startSlogansCallback) {
            startSlogansCallback();
        } else if (window.startSlogans) {
            window.startSlogans();
        }
    }
    
    clearSectorLock();
    
    const overlayEl = getEl('hud-overlay');
    if (overlayEl) overlayEl.classList.remove('active');

    const controlDeck = getEl('control-deck');
    if (controlDeck) controlDeck.style.display = 'none';

    isMitigatedMode = false;
    setMitigationHeld(false);

    const cmContainer = getEl('hud-bottom');
    if (cmContainer) cmContainer.classList.add('hud-hidden');
    
    // Smooth camera animation back to grid view
    animateCamera(camera.position, new THREE.Vector3(0, 0, 40), 1200);
    
    // Clear template cycle interval
    if (templateCycleInterval) {
        clearInterval(templateCycleInterval);
        templateCycleInterval = null;
    }
    
    // Restore all panels smoothly from their CURRENT positions to original positions
    panels.forEach((p, idx) => {
        // Stagger animations for smooth wave effect
        const delay = idx * 8; // Slightly longer stagger for more visible wave
        
        setTimeout(() => {
            // Store current position to ensure smooth animation from wherever panel is
            const from = { x: p.position.x, y: p.position.y, z: p.position.z };
            const to = p.userData.originalPosition;
            
            // Longer duration for smoother glide
            animatePosition(p, to, 1200);
            animateRotation(p, p.userData.originalRotation, 1200);
            animateScale(p, { x: 1, y: 1, z: 1 }, 1200);
            animateOpacity(p.material, 1, 1200);
        }, delay);
    });
    
    // Restore sector labels smoothly
    sectorLabelSprites.forEach(obj => {
        animateScale(obj, { x: 4, y: 1, z: 1 }, 1200);
        animateOpacity(obj.material, 0.9, 1200);
    });
    
    // Clear flag after animations complete
    setTimeout(() => {
        resetPanelsForGridView();
        isReturningToGrid = false;
    }, 1200 + (panels.length * 8));
    
    selectedPanel = null;
    sectorPanels = [];
}

function navigateSlide(direction) {
    if (!selectedPanel || sectorPanels.length === 0) return;
    
    // Find current panel in sector panels
    const currentSectorIndex = sectorPanels.indexOf(selectedPanel);
    const newSectorIndex = currentSectorIndex + direction;
    
    // Wrap around within sector
    if (newSectorIndex < 0 || newSectorIndex >= sectorPanels.length) return;
    
    const newPanel = sectorPanels[newSectorIndex];
    switchToPanel(newPanel);
}

function updateNavButtons() {
    if (!selectedPanel) return;
    const prevBtn = getEl('prev-btn');
    const nextBtn = getEl('next-btn');
    if (!prevBtn || !nextBtn) return;
    const currentSectorIndex = sectorPanels.indexOf(selectedPanel);
    prevBtn.disabled = currentSectorIndex <= 0;
    nextBtn.disabled = currentSectorIndex >= sectorPanels.length - 1;
}

function onKeyDown(event) {
    if (!isDetailView && event.ctrlKey && event.shiftKey && (event.key === 'o' || event.key === 'O')) {
        event.preventDefault();
        effectsBrowserToggle();
        return;
    }

    if (!isDetailView && effectsBrowserOpen && event.key === 'Escape') {
        event.preventDefault();
        effectsBrowserToggle(false);
        return;
    }

    if (!isDetailView && event.ctrlKey && event.shiftKey && (event.key === 'p' || event.key === 'P')) {
        event.preventDefault();
        pixelToggleEditMode();
        return;
    }

    if (!isDetailView && isPixelEditMode) {
        if (event.key === 'Escape') {
            event.preventDefault();
            pixelToggleEditMode();
            return;
        }
        if (event.key === '[') {
            event.preventDefault();
            pixelActiveColorIndex = Math.max(0, pixelActiveColorIndex - 1);
            pixelUpdateUI();
            return;
        }
        if (event.key === ']') {
            event.preventDefault();
            pixelActiveColorIndex = Math.min(pixelPalette.length - 1, pixelActiveColorIndex + 1);
            pixelUpdateUI();
            return;
        }
        if (event.key >= '0' && event.key <= '9') {
            event.preventDefault();
            pixelActiveColorIndex = Number(event.key);
            pixelUpdateUI();
            return;
        }
        const target = event.target;
        const targetTag = target ? target.tagName : '';
        const isTypingTarget = target && (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || targetTag === 'SELECT' || target.isContentEditable);
        if (!isTypingTarget && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && (event.key === 'i' || event.key === 'I')) {
            event.preventDefault();
            pixelCycleIntensity();
            return;
        }
        if (!isTypingTarget && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && (event.key === 'e' || event.key === 'E')) {
            event.preventDefault();
            pixelDownloadJSON();
            return;
        }
        if (!isTypingTarget && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && (event.key === 'u' || event.key === 'U')) {
            event.preventDefault();
            pixelCopyPxUrl();
            return;
        }
    }

    if (!isDetailView && !isPixelEditMode && !effectsBrowserOpen && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        event.stopPropagation();

        const items = effectsBrowserGetCatalog();
        if (!items.length) return;

        const sel = getEl('grid-effects-select');
        if (sel && sel.value) {
            const idx = items.findIndex((i) => i.id === sel.value);
            if (idx >= 0) effectsBrowserIndex = idx;
        }

        const dir = event.key === 'ArrowRight' ? 1 : -1;
        effectsBrowserIndex = (effectsBrowserIndex + dir + items.length) % items.length;
        const next = items[effectsBrowserIndex];
        if (sel && next) sel.value = next.id;
        effectsBrowserRun(next);
        return;
    }

    if (event.key === 'Escape' && isDetailView) returnToGrid();
    else if (event.key === 'ArrowUp' && isDetailView) returnToGrid();
    else if (event.key === 'ArrowDown' && isDetailView && selectedPanel) {
        const item = selectedPanel.userData ? selectedPanel.userData.item : null;
        if (!item || item.type !== 'prediction') {
            isMitigatedMode = false;
            setMitigationHeld(false);
            updateMitigationContent(item);
            return;
        }

        isMitigatedMode = !isMitigatedMode;
        setMitigationHeld(isMitigatedMode);
        updateMitigationContent(item);
    }
    else if (event.key === 'ArrowLeft' && isDetailView) navigateSlide(-1);
    else if (event.key === 'ArrowRight' && isDetailView) navigateSlide(1);
    else if ((event.key === 'm' || event.key === 'M') && isDetailView && selectedPanel) {
        const item = selectedPanel.userData ? selectedPanel.userData.item : null;
        if (!item || item.type !== 'prediction') {
            isMitigatedMode = false;
            setMitigationHeld(false);
            updateMitigationContent(item);
            return;
        }

        isMitigatedMode = !isMitigatedMode;
        setMitigationHeld(isMitigatedMode);
        updateMitigationContent(item);
    }
}

function onKeyUp(event) {
    // No longer needed for toggle behavior
}

function onMouseDown(event) {
    if (!isPixelEditMode || isDetailView) return;
    pixelPointerDown = true;
    pixelSuppressClick = false;
    pixelIsPainting = true;
    pixelIsErasing = event.shiftKey || event.button === 2;
    pixelLastPaintKey = null;
}

function onMouseUp() {
    if (!isPixelEditMode) return;
    pixelPointerDown = false;
    pixelIsPainting = false;
    pixelIsErasing = false;
    pixelLastPaintKey = null;
}

function onContextMenu(event) {
    if (!isPixelEditMode) return;
    event.preventDefault();
}

function animateCamera(from, to, duration) {
    const start = Date.now();
    const startPos = from.clone();
    function update() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = easeInOutCubic(progress);
        camera.position.lerpVectors(startPos, to, eased);
        if (progress < 1) requestAnimationFrame(update);
    }
    update();
}

function animatePosition(obj, to, duration) {
    const start = Date.now();
    const startPos = obj.position.clone();
    const target = new THREE.Vector3(to.x, to.y, to.z);
    function update() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = easeInOutCubic(progress);
        obj.position.lerpVectors(startPos, target, eased);
        if (progress < 1) requestAnimationFrame(update);
    }
    update();
}

function animateRotation(obj, to, duration) {
    const start = Date.now();
    const startRot = { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z };
    function update() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = easeInOutCubic(progress);
        obj.rotation.x = startRot.x + (to.x - startRot.x) * eased;
        obj.rotation.y = startRot.y + (to.y - startRot.y) * eased;
        obj.rotation.z = startRot.z + (to.z - startRot.z) * eased;
        if (progress < 1) requestAnimationFrame(update);
    }
    update();
}

function animateScale(obj, to, duration) {
    const start = Date.now();
    const startScale = { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z };
    function update() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = easeInOutCubic(progress);
        obj.scale.x = startScale.x + (to.x - startScale.x) * eased;
        obj.scale.y = startScale.y + (to.y - startScale.y) * eased;
        obj.scale.z = startScale.z + (to.z - startScale.z) * eased;
        if (progress < 1) requestAnimationFrame(update);
    }
    update();
}

function animateOpacity(material, to, duration) {
    const start = Date.now();
    const startOpacity = material.opacity;
    function update() {
        const progress = Math.min((Date.now() - start) / duration, 1);
        const eased = easeInOutCubic(progress);
        material.opacity = startOpacity + (to - startOpacity) * eased;
        material.transparent = true;
        if (progress < 1) requestAnimationFrame(update);
    }
    update();
}

function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
}

function animate() {
    if (!gridIsRunning) return;
    animationFrameId = requestAnimationFrame(animate);
    
    const time = Date.now() * 0.001;
    
    // Animate individual panels when in grid view
    if (!motionDisabled && !isDetailView && !isReturningToGrid) {
        // Update sweeping effect if active
        if (activeSweepingEffect) {
            const elapsed = Date.now() - activeSweepingEffect.startTime;
            if (elapsed >= activeSweepingEffect.duration) {
                if (typeof activeSweepingEffect.update === 'function') {
                    activeSweepingEffect.update(time, activeSweepingEffect.duration);
                }
                activeSweepingEffect = null;
            } else {
                activeSweepingEffect.update(time, elapsed);
            }
        }
        
        if (!isPixelEditMode) {
            // Update random effects
            updateActiveEffects(time);

            panels.forEach(panel => {
                // Skip base animations if panel has active effect
                if (activeEffects.has(panel)) {
                    // Still update shader uniforms
                    if (panel.material.uniforms) {
                        panel.material.uniforms.time.value = time;
                    }
                    return;
                }
                
                const userData = panel.userData;
                if (userData.originalPosition) {
                    // Subtle floating motion (up/down)
                    const floatY = Math.sin(time * userData.floatSpeed + userData.floatOffset) * userData.floatAmplitude;
                    // Subtle drift motion (left/right)
                    const driftX = Math.sin(time * userData.floatSpeed * 0.5 + userData.floatOffset) * 0.05;
                    
                    // Magnetic cursor interaction
                    const dx = mouseWorld.x - userData.originalPosition.x;
                    const dy = mouseWorld.y - userData.originalPosition.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    const maxDistance = 15; // Influence radius
                    
                    let magneticX = 0;
                    let magneticY = 0;
                    
                    if (distance < maxDistance) {
                        const strength = (1 - distance / maxDistance) * 0.3; // Magnetic strength
                        magneticX = (dx / distance) * strength;
                        magneticY = (dy / distance) * strength;
                    }
                    
                    panel.position.x = userData.originalPosition.x + driftX + magneticX;
                    panel.position.y = userData.originalPosition.y + floatY + magneticY;
                    
                    // Subtle rotation wobble
                    panel.rotation.z = Math.sin(time * 0.3 + userData.floatOffset) * 0.02;
                }
                
                // Update shader uniforms
                if (panel.material.uniforms) {
                    panel.material.uniforms.time.value = time;
                    // RGB shift increases with distance from center
                    const distFromCenter = Math.sqrt(panel.position.x * panel.position.x + panel.position.y * panel.position.y);
                    panel.material.uniforms.rgbShift.value = Math.min(distFromCenter * 0.5, 3);
                }
            });
        }
        // Slow camera orbit
        camera.rotation.y = Math.sin(time * 0.1) * 0.05;
    } else if (!isDetailView && isReturningToGrid) {
        // During return transition, only update shader uniforms
        panels.forEach(panel => {
            if (panel.material.uniforms) {
                panel.material.uniforms.time.value = time;
            }
        });
    } else {
        // Floating animation in detail view
        // Animate main panel and thumbnails
        if (selectedPanel) {
            const floatY = Math.sin(time * 0.3) * 0.1;
            const floatZ = Math.sin(time * 0.4) * 0.05;
            selectedPanel.position.z = 2 + floatZ;
        }
        
        sectorPanels.forEach((panel, idx) => {
            if (panel !== selectedPanel && panel.material && panel.material.opacity > 0) {
                const floatY = Math.sin(time * 0.4 + idx) * 0.08;
                const floatZ = Math.sin(time * 0.5 + idx) * 0.03;
                panel.position.z = 1 + floatZ;
            }
        });
        
        // Animate floating text panels with template-aware positioning
        // STOP ALL MOVEMENT if any panel is hovered
        if (!hoveredTextPanel) {
            textPanels.forEach(panel => {
                if (panel.userData.floatOffset !== undefined && panel.userData.baseY !== undefined) {
                    const floatY = Math.sin(time * panel.userData.floatSpeed + panel.userData.floatOffset) * 0.15;
                    const floatX = Math.sin(time * panel.userData.floatSpeed * 0.5 + panel.userData.floatOffset) * 0.1;
                    const floatZ = Math.cos(time * panel.userData.floatSpeed * 0.7 + panel.userData.floatOffset) * 0.1;
                    
                    // Use template base positions
                    panel.position.x = (panel.userData.baseX || 22) + floatX;
                    panel.position.y = panel.userData.baseY + floatY;
                    panel.position.z = (panel.userData.baseZ || 3) + floatZ;
                    
                    // Subtle rotation
                    panel.rotation.y = Math.sin(time * 0.2 + panel.userData.floatOffset) * 0.05;
                }
            });
        }
        
        // Update scanline overlay if present
        if (selectedPanel && selectedPanel.userData.scanlineOverlay) {
            const overlay = selectedPanel.userData.scanlineOverlay;
            overlay.position.copy(selectedPanel.position);
            overlay.position.z += 0.1;
            if (overlay.material.uniforms) {
                overlay.material.uniforms.time.value = time;
            }
        }
    }
    
    if (renderer) renderer.render(scene, camera);
}

// ========== RANDOM PANEL EFFECTS SYSTEM ==========

// Effect 1: The Bumper
function effectBumper(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 2000,
        velocity: { x: (Math.random() - 0.5) * 3, y: (Math.random() - 0.5) * 3 },
        update: (time, elapsed) => {
            if (elapsed < 500) {
                // Accelerate
                panel.position.x += effect.velocity.x * 0.1;
                panel.position.y += effect.velocity.y * 0.1;
                
                // Check collisions with neighbors
                panels.forEach(other => {
                    if (other !== panel && !activeEffects.has(other)) {
                        const dx = panel.position.x - other.position.x;
                        const dy = panel.position.y - other.position.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist < 6) {
                            // Trigger wobble on hit panel
                            effectShaker(other, 1000);
                        }
                    }
                });
            } else {
                // Return with bounce
                const t = (elapsed - 500) / 1500;
                const bounce = Math.sin(t * Math.PI);
                panel.position.x = panel.userData.originalPosition.x + effect.velocity.x * bounce;
                panel.position.y = panel.userData.originalPosition.y + effect.velocity.y * bounce;
            }
        }
    };
    return effect;
}

// Effect 2: The Zoom Burst
function effectZoomBurst(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 3000,
        update: (time, elapsed) => {
            if (elapsed < 800) {
                // Zoom in
                const t = elapsed / 800;
                const scale = 1 + t * 2;
                panel.scale.set(scale, scale, 1);
                panel.rotation.z = t * 0.3;
            } else if (elapsed < 2200) {
                // Hold
                panel.scale.set(3, 3, 1);
                panel.rotation.z = 0.3;
            } else {
                // Snap back with elastic
                const t = (elapsed - 2200) / 800;
                const elastic = Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
                panel.scale.set(elastic, elastic, 1);
                panel.rotation.z = 0.3 * (1 - t);
            }
        }
    };
    return effect;
}

// Effect 3: The Glitch Storm
function effectGlitchStorm(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 2500,
        update: (time, elapsed) => {
            if (panel.material.uniforms) {
                panel.material.uniforms.glitchIntensity.value = 1.0;
                panel.material.uniforms.rgbShift.value = 10;
                
                // Position jitter
                const jitter = 0.2;
                panel.position.x = panel.userData.originalPosition.x + (Math.random() - 0.5) * jitter;
                panel.position.y = panel.userData.originalPosition.y + (Math.random() - 0.5) * jitter;
            }
        }
    };
    return effect;
}

// Effect 4: The Spinner
function effectSpinner(panel) {
    const axis = Math.random() > 0.5 ? 'y' : 'z';
    const speed = Math.random() > 0.5 ? 2 : 4; // Slow or fast
    const effect = {
        startTime: Date.now(),
        duration: 2000,
        update: (time, elapsed) => {
            const t = elapsed / 2000;
            const rotation = t * Math.PI * speed;
            panel.rotation[axis] = rotation;
        }
    };
    return effect;
}

// Effect 5: The Depth Diver
function effectDepthDiver(panel) {
    const direction = Math.random() > 0.5 ? 1 : -1; // Forward or back
    const effect = {
        startTime: Date.now(),
        duration: 2500,
        update: (time, elapsed) => {
            if (elapsed < 1000) {
                // Dive
                const t = elapsed / 1000;
                panel.position.z = panel.userData.originalPosition.z + direction * t * 10;
            } else {
                // Return
                const t = (elapsed - 1000) / 1500;
                const ease = 1 - Math.pow(1 - t, 3);
                panel.position.z = panel.userData.originalPosition.z + direction * 10 * (1 - ease);
            }
        }
    };
    return effect;
}

// Effect 6: The Shaker
function effectShaker(panel, duration = 2000) {
    const effect = {
        startTime: Date.now(),
        duration: duration,
        update: (time, elapsed) => {
            const intensity = 1 - elapsed / duration;
            const shake = 0.3 * intensity;
            panel.position.x = panel.userData.originalPosition.x + (Math.random() - 0.5) * shake;
            panel.position.y = panel.userData.originalPosition.y + (Math.random() - 0.5) * shake;
        }
    };
    activeEffects.set(panel, effect);
    return effect;
}

// Effect 7: The Fader
function effectFader(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 3000,
        pulses: 3,
        update: (time, elapsed) => {
            const t = (elapsed / 3000) * effect.pulses;
            const opacity = 0.2 + 0.8 * Math.abs(Math.sin(t * Math.PI));
            if (panel.material.uniforms && panel.material.uniforms.panelOpacity) {
                panel.material.uniforms.panelOpacity.value = opacity;
            } else {
                panel.material.opacity = opacity;
                panel.material.transparent = true;
            }
        }
    };
    return effect;
}

// Effect 8: The Color Inverter
function effectColorInverter(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 2500,
        update: (time, elapsed) => {
            if (panel.material.uniforms) {
                // Flash between normal and inverted
                const flash = Math.floor((elapsed / 200) % 2);
                if (panel.material.uniforms.invertAmount) {
                    panel.material.uniforms.invertAmount.value = flash ? 1 : 0;
                }
                if (panel.material.uniforms.rgbShift) {
                    panel.material.uniforms.rgbShift.value = flash ? 2.5 : 0;
                }
                if (panel.material.uniforms.glitchIntensity) {
                    panel.material.uniforms.glitchIntensity.value = flash ? 0.7 : 0;
                }
            }
        }
    };
    return effect;
}

// Effect 9: The Orbit
function effectOrbit(panel) {
    const radius = 3;
    const effect = {
        startTime: Date.now(),
        duration: 3000,
        update: (time, elapsed) => {
            const t = Math.min(1, Math.max(0, elapsed / 3000));
            const angle = t * Math.PI * 2;

            // Ease radius back to 0 at the end so global cleanup doesn't cause a snap.
            const returnStart = 0.75;
            let r = radius;
            if (t >= returnStart) {
                const rt = (t - returnStart) / (1 - returnStart);
                const ease = 1 - Math.pow(1 - rt, 3);
                r = radius * (1 - ease);
            }

            panel.position.x = panel.userData.originalPosition.x + Math.cos(angle) * r;
            panel.position.y = panel.userData.originalPosition.y + Math.sin(angle) * r;
        }
    };
    return effect;
}

// Effect 10: The Ripple Trigger
function effectRippleTrigger(panel) {
    const effect = {
        startTime: Date.now(),
        duration: 2000,
        update: (time, elapsed) => {
            // Pulse the triggering panel
            const scale = 1 + Math.sin(elapsed / 2000 * Math.PI * 4) * 0.2;
            panel.scale.set(scale, scale, 1);
            
            // Trigger ripple on neighbors
            if (elapsed % 300 < 50) {
                panels.forEach(other => {
                    if (other !== panel) {
                        const dx = other.userData.originalPosition.x - panel.userData.originalPosition.x;
                        const dy = other.userData.originalPosition.y - panel.userData.originalPosition.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        
                        if (dist < 12 && !activeEffects.has(other)) {
                            // Trigger wave effect
                            const delay = dist * 50;
                            setTimeout(() => {
                                const wave = {
                                    startTime: Date.now(),
                                    duration: 500,
                                    update: (t, e) => {
                                        const s = 1 + Math.sin(e / 500 * Math.PI) * 0.3;
                                        other.scale.set(s, s, 1);
                                    }
                                };
                                activeEffects.set(other, wave);
                            }, delay);
                        }
                    }
                });
            }
        }
    };
    return effect;
}

const retroIcons = {
    games_12x7: [
        '····████····',
        '···██··██···',
        '···██··██···',
        '····████····',
        '·····██·····',
        '·····██·····',
        '····████····'
    ],
    film_12x7: [
        '··████████··',
        '·██████████·',
        '·██··██··██·',
        '·██████████·',
        '··████████··',
        '····██··██··',
        '····██··██··'
    ],
    craft_12x7: [
        '········████',
        '········████',
        '████████████',
        '████████████',
        '····██······',
        '····██······',
        '····██······'
    ],
    music_12x7: [
        '··········██',
        '·········███',
        '········████',
        '···████·████',
        '··██████····',
        '··██████····',
        '···████·····'
    ],
    live_music_12x7: [
        '····████····',
        '···██████···',
        '···██████···',
        '···██████···',
        '····████····',
        '█████··█████',
        '·███████████'
    ],
    fashion_12x7: [
        '····████····',
        '···██████···',
        '···██████···',
        '··████████··',
        '··████████··',
        '···██··██···',
        '··██····██··'
    ],
    ai_12x7: [
        '·██████████·',
        '██··█··█··██',
        '██··█··█··██',
        '████████████',
        '·██████████·',
        '··████████··',
        '··██····██··'
    ],
    ai_infrastructure_12x7: [
        '████████████',
        '█··█··█··█··',
        '████████████',
        '█··█··█··█··',
        '████████████',
        '█··█··█··█··',
        '████████████'
    ],
    creator_12x7: [
        '██··████████',
        '███·████████',
        '████████████',
        '████████████',
        '·███████████',
        '··██████████',
        '···█████████'
    ],
    social_12x7: [
        '··████████··',
        '·██······██·',
        '██········██',
        '██········██',
        '·██······██·',
        '··████████··',
        '···██·······'
    ],
    education_12x7: [
        '████████████',
        '██········██',
        '██········██',
        '██········██',
        '██········██',
        '██········██',
        '████████████'
    ],
    advertising_12x7: [
        '█···········',
        '██··········',
        '███·········',
        '████████████',
        '███·········',
        '██··········',
        '█···········'
    ],
    year_2029_12x7: [
        '███·····███·',
        '··██···██·██',
        '··██··██··██',
        '··██··█████·',
        '··██··██··██',
        '··██··██··██',
        '███···███·██'
    ]
};

const initialRetroIcons = (() => {
    try {
        return JSON.parse(JSON.stringify(retroIcons));
    } catch (e) {
        return null;
    }
})();

function restoreRetroIcons() {
    if (!initialRetroIcons) return;
    Object.keys(retroIcons).forEach((k) => {
        if (!Object.prototype.hasOwnProperty.call(initialRetroIcons, k)) {
            delete retroIcons[k];
        }
    });
    Object.keys(initialRetroIcons).forEach((k) => {
        const v = initialRetroIcons[k];
        retroIcons[k] = Array.isArray(v) ? v.slice() : v;
    });
}

function fadeSectorLabels(selectedSector) {
    sectorLabelSprites.forEach(sprite => {
        if (!selectedSector) {
            animateScale(sprite, { x: 4, y: 1, z: 1 }, 600);
            animateOpacity(sprite.material, 0.9, 600);
        } else if (lockedSector && sprite.userData.sector === lockedSector) {
            animateScale(sprite, { x: 5.4, y: 1.25, z: 1 }, 600);
            animateOpacity(sprite.material, 1, 600);
        } else if (sprite.userData.sector === selectedSector) {
            animateScale(sprite, { x: 5.4, y: 1.25, z: 1 }, 600);
            animateOpacity(sprite.material, 1, 600);
        } else {
            animateOpacity(sprite.material, 0.05, 600);
        }
    });
}

function renderPixelIcon(iconData, scale = 40) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const rows = iconData.length;
    const cols = iconData[0] ? iconData[0].length : 0;

    canvas.width = cols * scale;
    canvas.height = rows * scale;
    
    ctx.fillStyle = '#444444';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    iconData.forEach((row, y) => {
        for (let x = 0; x < row.length; x++) {
            const char = row[x];
            ctx.fillStyle = char === '█' ? '#000000' : '#FFFFFF';
            ctx.fillRect(x * scale, y * scale, scale, scale);
        }
    });
    
    return canvas;
}

// Collective icon flip effect - entire grid becomes one giant icon
function effectIconFlip(triggerPanel) {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;

    const presets = window.pixelPresets || {};
    const presetIds = Object.keys(presets);
    const matchingPresetIds = presetIds.filter((id) => {
        const p = presets[id];
        if (!p || p.v !== 1) return false;
        if (p.cols !== gridCols) return false;
        return true;
    });
    const pickedPresetId = (matchingPresetIds.length > 0)
        ? matchingPresetIds[Math.floor(Math.random() * matchingPresetIds.length)]
        : null;
    const pickedPreset = pickedPresetId ? presets[pickedPresetId] : null;

    const iconNames = Object.keys(retroIcons);
    const matchingIcons = iconNames.filter(name => {
        const icon = retroIcons[name];
        return icon && icon.length <= gridRows && icon[0] && icon[0].length === gridCols;
    });
    const randomIcon = (matchingIcons.length > 0)
        ? matchingIcons[Math.floor(Math.random() * matchingIcons.length)]
        : iconNames[Math.floor(Math.random() * iconNames.length)];
    const iconData = retroIcons[randomIcon];
    
    
    // Create solid color textures that work with shader material
    // Use larger canvas and ensure proper format
    const yellowCanvas = document.createElement('canvas');
    yellowCanvas.width = 1024;
    yellowCanvas.height = 1024;
    const yellowCtx = yellowCanvas.getContext('2d');
    yellowCtx.fillStyle = '#ffff66';
    yellowCtx.fillRect(0, 0, 1024, 1024);
    const yellowTexture = new THREE.CanvasTexture(yellowCanvas);
    yellowTexture.needsUpdate = true;
    
    const blackCanvas = document.createElement('canvas');
    blackCanvas.width = 1024;
    blackCanvas.height = 1024;
    const blackCtx = blackCanvas.getContext('2d');
    blackCtx.fillStyle = '#000000';
    blackCtx.fillRect(0, 0, 1024, 1024);
    const blackTexture = new THREE.CanvasTexture(blackCanvas);
    blackTexture.needsUpdate = true;
    
    const startTime = Date.now();
    const duration = 3000;

    const iconColorIndex = 1;
    const iconCells = (!pickedPreset && pixelGrid && iconData)
        ? pixelDecodeRetroIconToCells(iconData, pixelGrid.cols, pixelGrid.rows, iconColorIndex)
        : null;
    const pushed = (!!pixelGrid) ? pixelPushTransientState() : false;
    let applied = false;
    let restored = false;
    
    // Store panels affected
    const panelData = [];
    const gridCols2 = gridDimensions.cols;
    const gridRows2 = gridDimensions.rows;
    
    // Panels are organized by SECTOR (column), not row
    // Panel layout: [col0: 0-4, col1: 5-9, col2: 10-14, ...]
    // Icon layout: row-by-row [row0: col0-10, row1: col0-10, ...]
    
    // Build a grid map and assign colors
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows2; row++) {
        for (let col = 0; col < gridCols2; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                panelData.push({ panel });
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;

            if (!applied && pixelGrid) {
                if (pickedPreset) {
                    pixelApplyPreset(pickedPreset);
                    pixelForceBinaryDisplay = true;
                    pixelUpdateAllOverlays();
                    applied = true;
                } else if (iconCells) {
                    pixelGrid.cells.set(iconCells);
                    pixelForceBinaryDisplay = true;
                    pixelUpdateAllOverlays();
                    applied = true;
                }
            }
            
            panelData.forEach(data => {
                if (progress < 0.2) {
                    // Flip to icon
                    const flipProgress = progress / 0.2;
                    data.panel.rotation.y = Math.PI * flipProgress;
                } else if (progress > 0.8) {
                    // Flip back
                    const flipProgress = (progress - 0.8) / 0.2;
                    data.panel.rotation.y = Math.PI + (Math.PI * flipProgress);

                    if (flipProgress > 0.5 && !restored) {
                        if (pushed) pixelPopTransientState();
                        restored = true;
                    }
                } else {
                    // Hold - icon should be visible
                    data.panel.rotation.y = Math.PI;
                }
            });
            
            // Cleanup when done
            if (progress >= 1) {
                if (!restored) {
                    if (pushed) pixelPopTransientState();
                    restored = true;
                }
                panelData.forEach(data => {
                    data.panel.rotation.y = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// ============================================
// GRAND SWEEPING GRID ANIMATIONS
// ============================================

// 1. SPIRAL - Panels animate outward from center in a spiral pattern
function effectSpiral() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    const centerX = gridCols / 2;
    const centerY = gridRows / 2;
    
    const startTime = Date.now();
    const duration = 3000;
    
    const panelData = [];
    
    // Build grid map
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    // Calculate spiral order for each panel
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                // Calculate angle and distance from center
                const dx = col - centerX;
                const dy = row - centerY;
                const angle = Math.atan2(dy, dx);
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                // Spiral delay: combine angle and distance
                const spiralOrder = angle + distance * 0.5;
                
                panelData.push({
                    panel,
                    delay: spiralOrder * 100,
                    originalScale: { ...panel.scale }
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            panelData.forEach(data => {
                const localElapsed = elapsed - data.delay;
                if (localElapsed > 0 && localElapsed < duration) {
                    const progress = localElapsed / duration;
                    
                    // Pulse scale
                    const scale = 1 + Math.sin(progress * Math.PI) * 0.3;
                    data.panel.scale.set(scale, scale, 1);
                    
                    // Rotate
                    data.panel.rotation.z = progress * Math.PI * 2;
                }
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.scale.set(1, 1, 1);
                    data.panel.rotation.z = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 2. WAVE - Sine wave ripples across the grid
function effectWave() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    
    const startTime = Date.now();
    const duration = 3000;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                panelData.push({
                    panel,
                    col,
                    row,
                    originalZ: panel.position.z
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            
            panelData.forEach(data => {
                // Horizontal wave
                const wavePhase = (data.col / gridCols) * Math.PI * 2 - progress * Math.PI * 4;
                const amplitude = Math.sin(wavePhase) * 2;
                
                data.panel.position.z = data.originalZ + amplitude;
                data.panel.rotation.x = amplitude * 0.2;
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.z = data.originalZ;
                    data.panel.rotation.x = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 3. RIPPLE - Concentric circles from center
function effectRipple() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    const centerX = gridCols / 2;
    const centerY = gridRows / 2;
    
    const startTime = Date.now();
    const duration = 2500;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                const dx = col - centerX;
                const dy = row - centerY;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                panelData.push({
                    panel,
                    distance,
                    originalZ: panel.position.z
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            
            panelData.forEach(data => {
                // Ripple wave based on distance
                const ripplePhase = progress * 10 - data.distance * 0.5;
                const amplitude = Math.sin(ripplePhase * Math.PI) * 2;
                const fade = Math.max(0, 1 - Math.abs(ripplePhase - 5) / 5);
                
                data.panel.position.z = data.originalZ + amplitude * fade;
                data.panel.material.opacity = 0.5 + fade * 0.5;
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.z = data.originalZ;
                    data.panel.material.opacity = 1;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 4. DIAGONAL SWEEP - Panels animate in diagonal lines
function effectDiagonalSweep() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    
    const startTime = Date.now();
    const maxDiagonalIndex = (gridRows - 1) + (gridCols - 1);
    const maxDelay = maxDiagonalIndex * 80;
    const flipDuration = 1000;
    const duration = maxDelay + flipDuration;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                // Diagonal order: sum of row and col
                const diagonalIndex = row + col;
                
                panelData.push({
                    panel,
                    delay: diagonalIndex * 80
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            panelData.forEach(data => {
                const localElapsed = elapsed - data.delay;
                if (localElapsed > 0 && localElapsed < flipDuration) {
                    const progress = localElapsed / flipDuration;
                    const flip = progress < 0.5
                        ? (progress / 0.5) * Math.PI
                        : Math.PI + ((progress - 0.5) / 0.5) * Math.PI;

                    const op = 0.3 + Math.sin(progress * Math.PI) * 0.7;
                    if (data.panel.material.uniforms && data.panel.material.uniforms.panelOpacity) {
                        data.panel.material.uniforms.panelOpacity.value = op;
                    } else {
                        data.panel.material.opacity = op;
                        data.panel.material.transparent = true;
                    }
                    data.panel.rotation.y = flip;
                } else if (localElapsed >= flipDuration) {
                    if (data.panel.material.uniforms && data.panel.material.uniforms.panelOpacity) {
                        data.panel.material.uniforms.panelOpacity.value = 1;
                    } else {
                        data.panel.material.opacity = 1;
                        data.panel.material.transparent = true;
                    }
                    data.panel.rotation.y = 0;
                }
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    if (data.panel.material.uniforms && data.panel.material.uniforms.panelOpacity) {
                        data.panel.material.uniforms.panelOpacity.value = 1;
                    } else {
                        data.panel.material.opacity = 1;
                        data.panel.material.transparent = true;
                    }
                    data.panel.rotation.y = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 5. CHECKERBOARD PULSE - Alternating panels flash
function effectCheckerboard() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    
    const startTime = Date.now();
    const duration = 2000;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                const isEven = (row + col) % 2 === 0;
                
                panelData.push({
                    panel,
                    isEven,
                    originalZ: panel.position.z
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            const pulseFreq = Math.sin(progress * Math.PI * 4);
            
            panelData.forEach(data => {
                const pulse = data.isEven ? pulseFreq : -pulseFreq;
                data.panel.position.z = data.originalZ + pulse * 1.5;
                data.panel.material.opacity = 0.5 + Math.abs(pulse) * 0.5;
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.z = data.originalZ;
                    data.panel.material.opacity = 1;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 6. RADIAL BURST - All panels explode outward from center
function effectRadialBurst() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    const centerX = gridCols / 2;
    const centerY = gridRows / 2;
    
    const startTime = Date.now();
    const duration = 2500;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                const dx = col - centerX;
                const dy = row - centerY;
                const angle = Math.atan2(dy, dx);
                
                panelData.push({
                    panel,
                    angle,
                    originalX: panel.position.x,
                    originalY: panel.position.y,
                    originalZ: panel.position.z
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            const burstAmount = Math.sin(progress * Math.PI) * 3;
            
            panelData.forEach(data => {
                data.panel.position.x = data.originalX + Math.cos(data.angle) * burstAmount;
                data.panel.position.y = data.originalY + Math.sin(data.angle) * burstAmount;
                data.panel.position.z = data.originalZ + burstAmount;
                data.panel.rotation.z = progress * Math.PI * 2;
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.x = data.originalX;
                    data.panel.position.y = data.originalY;
                    data.panel.position.z = data.originalZ;
                    data.panel.rotation.z = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 7. SNAKE - A path weaves through the grid
function effectSnake() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    
    const startTime = Date.now();
    const duration = 3500;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    // Create snake path (zigzag pattern)
    let snakeOrder = 0;
    for (let row = 0; row < gridRows; row++) {
        const cols = row % 2 === 0 ? 
            Array.from({length: gridCols}, (_, i) => i) : 
            Array.from({length: gridCols}, (_, i) => gridCols - 1 - i);
        
        for (const col of cols) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                panelData.push({
                    panel,
                    order: snakeOrder++
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            const wavePosition = progress * (panelData.length + 10);
            
            panelData.forEach(data => {
                const distance = Math.abs(data.order - wavePosition);
                const intensity = Math.max(0, 1 - distance / 5);
                
                if (intensity > 0) {
                    data.panel.scale.set(1 + intensity * 0.5, 1 + intensity * 0.5, 1);
                    data.panel.material.opacity = 0.3 + intensity * 0.7;
                } else {
                    data.panel.scale.set(1, 1, 1);
                    data.panel.material.opacity = 1;
                }
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.scale.set(1, 1, 1);
                    data.panel.material.opacity = 1;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 8. COLLAPSE/EXPAND - Grid collapses to center then expands
function effectCollapseExpand() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    const centerX = gridCols / 2;
    const centerY = gridRows / 2;
    
    const startTime = Date.now();
    const duration = 3000;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                const dx = col - centerX;
                const dy = row - centerY;
                
                panelData.push({
                    panel,
                    dx,
                    dy,
                    originalX: panel.position.x,
                    originalY: panel.position.y,
                    originalScale: { ...panel.scale }
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            const progress = elapsed / duration;
            
            // Collapse then expand
            let collapseAmount;
            if (progress < 0.5) {
                collapseAmount = progress * 2; // 0 to 1
            } else {
                collapseAmount = 1 - (progress - 0.5) * 2; // 1 to 0
            }
            
            panelData.forEach(data => {
                data.panel.position.x = data.originalX - data.dx * collapseAmount * 5;
                data.panel.position.y = data.originalY - data.dy * collapseAmount * 5;
                
                const scale = 1 - collapseAmount * 0.7;
                data.panel.scale.set(scale, scale, 1);
                data.panel.material.opacity = 0.3 + (1 - collapseAmount) * 0.7;
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.x = data.originalX;
                    data.panel.position.y = data.originalY;
                    data.panel.scale.set(1, 1, 1);
                    data.panel.material.opacity = 1;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

// 9. CASCADE - Columns or rows animate in sequence
function effectCascade() {
    const gridCols = gridDimensions.cols || 12;
    const gridRows = gridDimensions.rows || 7;
    const useColumns = Math.random() > 0.5;
    
    const startTime = Date.now();
    const duration = 2500;
    
    const panelData = [];
    
    const gridMap = {};
    panels.forEach(panel => {
        const col = panel.userData.sectorColumn;
        const row = panel.userData.rowInSector;
        const key = `${row},${col}`;
        gridMap[key] = panel;
    });
    
    for (let row = 0; row < gridRows; row++) {
        for (let col = 0; col < gridCols; col++) {
            const key = `${row},${col}`;
            const panel = gridMap[key];
            if (panel) {
                const cascadeIndex = useColumns ? col : row;
                
                panelData.push({
                    panel,
                    delay: cascadeIndex * 120,
                    originalY: panel.position.y
                });
                
                activeEffects.set(panel, { startTime, duration, update: () => {} });
            }
        }
    }
    
    return {
        startTime,
        duration,
        update: (time, elapsed) => {
            panelData.forEach(data => {
                const localElapsed = elapsed - data.delay;
                if (localElapsed > 0 && localElapsed < 800) {
                    const progress = localElapsed / 800;
                    
                    // Drop and bounce
                    const bounce = Math.sin(progress * Math.PI);
                    data.panel.position.y = data.originalY - bounce * 5;
                    data.panel.rotation.x = bounce * Math.PI * 0.5;
                }
            });
            
            if (elapsed >= duration) {
                panelData.forEach(data => {
                    data.panel.position.y = data.originalY;
                    data.panel.rotation.x = 0;
                    activeEffects.delete(data.panel);
                });
            }
        }
    };
}

function startDemoSequence() {
    if (demoSequenceActive) return;
    if (panels.length === 0) {
        console.log('No panels yet - wait for grid to load');
        return;
    }
    
    demoSequenceActive = true;
    demoSequenceIndex = 0;
    
    const sweepingEffects = [
        { name: 'Spiral', fn: effectSpiral, duration: 3000 },
        { name: 'Wave', fn: effectWave, duration: 3000 },
        { name: 'Ripple', fn: effectRipple, duration: 2500 },
        { name: 'Diagonal Sweep', fn: effectDiagonalSweep, duration: 2000 },
        { name: 'Checkerboard', fn: effectCheckerboard, duration: 2000 },
        { name: 'Radial Burst', fn: effectRadialBurst, duration: 2500 },
        { name: 'Snake', fn: effectSnake, duration: 3500 },
        { name: 'Collapse/Expand', fn: effectCollapseExpand, duration: 3000 },
        { name: 'Cascade', fn: effectCascade, duration: 2500 },
        { name: 'Pixel Preset', fn: effectPixelPresetShow, duration: 2600 },
        { name: 'Pixel Marquee', fn: effectPixelMarquee, duration: 5200 }
    ];
    
    function playNext() {
        if (demoSequenceIndex >= sweepingEffects.length) {
            demoSequenceActive = false;
            return;
        }
        
        const current = sweepingEffects[demoSequenceIndex];
        activeSweepingEffect = current.fn();
        
        demoSequenceIndex++;
        setTimeout(playNext, current.duration + 500);
    }
    
    playNext();
}

// Random Effect Scheduler
function startRandomEffects() {
    if (randomEffectsIntervalId) return;
    if (motionDisabled) return;

    const params = new URLSearchParams(window.location.search);
    const fxMode = (params.get('fx') || '').toLowerCase();
    const sequenceMode = fxMode === 'sequence';

    randomEffectsIntervalId = window.setInterval(() => {
        if (effectsBrowserOpen) return;
        if (!isDetailView && !isPixelEditMode && !isReturningToGrid && panels.length > 0 && !demoSequenceActive) {
            if (sequenceMode) {
                if (activeSweepingEffect) return;
                const now = Date.now();
                if (now < fxSequenceNextAt) return;

                const presets = window.pixelPresets || {};
                const presetIds = Object.keys(presets).sort();
                if (presetIds.length === 0) return;

                let next;
                if (fxSequenceStep % 2 === 0) {
                    const presetIndex = Math.floor(fxSequenceStep / 2) % presetIds.length;
                    const presetId = presetIds[presetIndex];
                    next = effectPixelPresetShow(presetId);
                } else {
                    next = effectPixelMarquee();
                }

                fxSequenceStep += 1;
                if (next) {
                    activeSweepingEffect = next;
                    fxSequenceNextAt = now + (next.duration || 3000) + 600;
                } else {
                    fxSequenceNextAt = now + 1200;
                }
                return;
            }

            // Occasionally trigger grand sweeping animations (20% chance - increased for testing)
            if (Math.random() < 0.2) {
                const sweepingEffects = [
                    effectSpiral,
                    effectWave,
                    effectRipple,
                    effectDiagonalSweep,
                    effectCheckerboard,
                    effectRadialBurst,
                    effectSnake,
                    effectCollapseExpand,
                    effectCascade,
                    effectPixelPresetShow,
                    effectPixelMarquee
                ];
                
                const randomSweep = sweepingEffects[Math.floor(Math.random() * sweepingEffects.length)];
                console.log('Triggering sweeping effect:', randomSweep.name);
                const maybe = randomSweep();
                if (maybe) {
                    activeSweepingEffect = maybe;
                }
                console.log('activeSweepingEffect set:', activeSweepingEffect);
            } else {
                // Normal individual panel effects
                const numEffects = Math.floor(Math.random() * 3) + 1;
                
                for (let i = 0; i < numEffects; i++) {
                    const randomPanel = panels[Math.floor(Math.random() * panels.length)];
                    
                    // Don't apply effect if panel already has one
                    if (!activeEffects.has(randomPanel)) {
                        // Pick random effect - added icon flip
                        const effects = [
                            effectBumper,
                            effectZoomBurst,
                            effectGlitchStorm,
                            effectSpinner,
                            effectDepthDiver,
                            effectShaker,
                            effectFader,
                            effectColorInverter,
                            effectOrbit,
                            effectRippleTrigger,
                            effectIconFlip
                        ];
                        
                        const randomEffect = effects[Math.floor(Math.random() * effects.length)];
                        const effect = randomEffect(randomPanel);
                        activeEffects.set(randomPanel, effect);
                    }
                }
            }
        } else if (isDetailView) {
            // In detail view, clear any active effects on sector panels to prevent interference
            sectorPanels.forEach(panel => {
                if (activeEffects.has(panel)) {
                    activeEffects.delete(panel);
                    // Reset panel opacity
                    panel.material.opacity = 1;
                    panel.material.transparent = true;
                }
            });
        }
    }, 3000); // Trigger new effects every 3 seconds
}

function effectsBrowserGetCatalog() {
    if (effectsBrowserCatalog) return effectsBrowserCatalog;

    const items = [
        { category: 'Sweep', name: 'Spiral', kind: 'sweep', fn: effectSpiral },
        { category: 'Sweep', name: 'Wave', kind: 'sweep', fn: effectWave },
        { category: 'Sweep', name: 'Ripple', kind: 'sweep', fn: effectRipple },
        { category: 'Sweep', name: 'Diagonal Sweep', kind: 'sweep', fn: effectDiagonalSweep },
        { category: 'Sweep', name: 'Checkerboard', kind: 'sweep', fn: effectCheckerboard },
        { category: 'Sweep', name: 'Radial Burst', kind: 'sweep', fn: effectRadialBurst },
        { category: 'Sweep', name: 'Snake', kind: 'sweep', fn: effectSnake },
        { category: 'Sweep', name: 'Collapse/Expand', kind: 'sweep', fn: effectCollapseExpand },
        { category: 'Sweep', name: 'Cascade', kind: 'sweep', fn: effectCascade },

        { category: 'Pixel', name: 'Pixel Preset Show', kind: 'sweep', fn: () => effectPixelPresetShow() },
        { category: 'Pixel', name: 'Pixel Marquee', kind: 'sweep', fn: effectPixelMarquee },

        { category: 'Panel', name: 'Bumper', kind: 'panel', fn: effectBumper },
        { category: 'Panel', name: 'Zoom Burst', kind: 'panel', fn: effectZoomBurst },
        { category: 'Panel', name: 'Glitch Storm', kind: 'panel', fn: effectGlitchStorm },
        { category: 'Panel', name: 'Spinner', kind: 'panel', fn: effectSpinner },
        { category: 'Panel', name: 'Depth Diver', kind: 'panel', fn: effectDepthDiver },
        { category: 'Panel', name: 'Shaker', kind: 'panel', fn: effectShaker },
        { category: 'Panel', name: 'Fader', kind: 'panel', fn: effectFader },
        { category: 'Panel', name: 'Color Inverter', kind: 'panel', fn: effectColorInverter },
        { category: 'Panel', name: 'Orbit', kind: 'panel', fn: effectOrbit },
        { category: 'Panel', name: 'Ripple Trigger', kind: 'panel', fn: effectRippleTrigger },

    ];

    const categoryOrder = { Sweep: 1, Pixel: 2, Panel: 3, Collective: 4 };
    items.sort((a, b) => {
        const ca = categoryOrder[a.category] || 99;
        const cb = categoryOrder[b.category] || 99;
        if (ca !== cb) return ca - cb;
        return a.name.localeCompare(b.name);
    });

    effectsBrowserCatalog = items.map((it, idx) => ({
        ...it,
        number: idx + 1,
        id: `${String(it.category).toLowerCase()}:${String(it.name).toLowerCase().replace(/\s+/g, '-')}`
    }));

    return effectsBrowserCatalog;
}

function effectsBrowserPickPanel() {
    if (hoveredPanel) return hoveredPanel;
    if (selectedPanel) return selectedPanel;
    if (panels && panels.length > 0) return panels[Math.floor(Math.random() * panels.length)];
    return null;
}

function effectsBrowserStopAll() {
    if (!isDetailView) {
        try {
            resetPanelsForGridView();
        } catch (e) {
        }
    }
    activeSweepingEffect = null;
    try {
        pixelRestoreAllTransientStates();
    } catch (e) {
    }
    try {
        pixelUpdateAllOverlays();
    } catch (e) {
    }
}

function effectsBrowserRun(item) {
    if (!item) return;
    effectsBrowserStopAll();

    if (item.kind === 'sweep') {
        const eff = item.fn ? item.fn() : null;
        if (eff) activeSweepingEffect = eff;
        return;
    }

    const panel = effectsBrowserPickPanel();
    if (!panel || !item.fn) return;
    const eff = item.fn(panel);
    if (eff) {
        activeEffects.set(panel, eff);
    }
}

function effectsBrowserEnsureUI() {
    if (effectsBrowserUI) return;

    const el = document.createElement('div');
    el.id = 'grid-effects-browser-ui';
    el.style.position = 'fixed';
    el.style.left = '20px';
    el.style.top = '20px';
    el.style.zIndex = '1200';
    el.style.background = 'rgba(0,0,0,0.92)';
    el.style.border = '2px solid #FFFF00';
    el.style.padding = '10px 12px';
    el.style.fontFamily = 'Courier New, monospace';
    el.style.fontSize = '12px';
    el.style.color = '#FFFF00';
    el.style.display = 'none';
    el.style.minWidth = '360px';
    el.style.maxWidth = '520px';

    el.innerHTML = `
        <div style="display:flex; justify-content:space-between; gap:10px; align-items:center; margin-bottom:8px;">
            <div style="font-weight:bold;">GRID EFFECTS</div>
            <div style="opacity:0.85; font-size:11px;">Ctrl+Shift+O</div>
        </div>
        <div style="display:flex; gap:6px; align-items:center;">
            <select id="grid-effects-select" style="flex:1; background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:4px 6px; font-family:Courier New, monospace; font-size:12px;"></select>
            <button id="grid-effects-run" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:4px 8px; cursor:pointer;">RUN</button>
        </div>
        <div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:8px;">
            <button id="grid-effects-next" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:4px 8px; cursor:pointer;">NEXT</button>
            <button id="grid-effects-stop" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:4px 8px; cursor:pointer;">STOP</button>
            <button id="grid-effects-close" type="button" style="background:#000; color:#FFFF00; border:1px solid #FFFF00; padding:4px 8px; cursor:pointer;">CLOSE</button>
        </div>
        <div style="margin-top:8px; opacity:0.8; line-height:1.4; font-size:11px;">
            <div>Runs sweeping effects on the whole grid or panel effects on the hovered/selected/random panel.</div>
        </div>
    `;

    const mountTarget = domRootEl || document.body;
    mountTarget.appendChild(el);
    effectsBrowserUI = el;

    const sel = getEl('grid-effects-select');
    const runBtn = getEl('grid-effects-run');
    const nextBtn = getEl('grid-effects-next');
    const stopBtn = getEl('grid-effects-stop');
    const closeBtn = getEl('grid-effects-close');

    const catalog = effectsBrowserGetCatalog();

    const groups = {};
    catalog.forEach((item) => {
        if (!groups[item.category]) groups[item.category] = [];
        groups[item.category].push(item);
    });

    const order = ['Sweep', 'Pixel', 'Panel', 'Collective'];
    const frag = document.createDocumentFragment();
    order.forEach((cat) => {
        const list = groups[cat] || [];
        if (list.length === 0) return;
        const optGroup = document.createElement('optgroup');
        optGroup.label = cat;
        list.forEach((item) => {
            const opt = document.createElement('option');
            opt.value = item.id;
            opt.textContent = `${String(item.number).padStart(2, '0')}. ${item.name}`;
            optGroup.appendChild(opt);
        });
        frag.appendChild(optGroup);
    });
    if (sel) {
        sel.innerHTML = '';
        sel.appendChild(frag);
        sel.value = catalog[0] ? catalog[0].id : '';
    }

    const getSelected = () => {
        const id = sel ? sel.value : '';
        const items = effectsBrowserGetCatalog();
        return items.find((i) => i.id === id) || null;
    };

    if (runBtn) {
        runBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            effectsBrowserRun(getSelected());
        });
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const items = effectsBrowserGetCatalog();
            if (!items.length) return;
            effectsBrowserIndex = (effectsBrowserIndex + 1) % items.length;
            const next = items[effectsBrowserIndex];
            if (sel && next) sel.value = next.id;
            effectsBrowserRun(next);
        });
    }
    if (stopBtn) {
        stopBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            effectsBrowserStopAll();
        });
    }
    if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            effectsBrowserToggle(false);
        });
    }
}

function effectsBrowserToggle(forceState) {
    effectsBrowserEnsureUI();
    effectsBrowserOpen = (typeof forceState === 'boolean') ? forceState : !effectsBrowserOpen;
    if (effectsBrowserUI) {
        effectsBrowserUI.style.display = effectsBrowserOpen ? 'block' : 'none';
    }
}

// Update active effects in animate loop
function updateActiveEffects(time) {
    activeEffects.forEach((effect, panel) => {
        const elapsed = Date.now() - effect.startTime;
        
        if (elapsed >= effect.duration) {
            if (typeof effect.update === 'function') {
                effect.update(time, effect.duration);
            }
            // Effect finished - cleanup
            activeEffects.delete(panel);
            
            // Reset panel to original state
            if (panel.userData.originalPosition) {
                panel.position.x = panel.userData.originalPosition.x;
                panel.position.y = panel.userData.originalPosition.y;
                panel.position.z = panel.userData.originalPosition.z;
            }
            panel.scale.set(1, 1, 1);
            panel.rotation.x = 0;
            panel.rotation.y = 0;
            panel.rotation.z = 0;
            if (panel.material.uniforms) {
                panel.material.uniforms.glitchIntensity.value = 0;
                panel.material.uniforms.rgbShift.value = 0;
                if (panel.material.uniforms.invertAmount) {
                    panel.material.uniforms.invertAmount.value = 0;
                }
                if (panel.material.uniforms.panelOpacity) {
                    panel.material.uniforms.panelOpacity.value = 1;
                }
            }
            panel.material.opacity = 1;
        } else {
            // Update effect
            effect.update(time, elapsed);
        }
    });
}

export function predictionsGridInit({ canvasContainer, domRoot, startSlogans, stopSlogans, assetsBaseUrl: assetsBaseUrlInput, gridData: gridDataInput, sectorColors: sectorColorsInput, sectorOrder: sectorOrderInput, pixelPresets: pixelPresetsInput } = {}) {
    if (gridInitDone) return;
    gridInitDone = true;
    if (typeof window !== 'undefined') {
        hadWindowIsDetailView = Object.prototype.hasOwnProperty.call(window, 'isDetailView');
        prevWindowIsDetailView = window.isDetailView;
        hadWindowPixelPresets = Object.prototype.hasOwnProperty.call(window, 'pixelPresets');
        prevWindowPixelPresets = window.pixelPresets;
    }
    try {
        if (typeof window !== 'undefined') {
            if (pixelPresetsInput && typeof pixelPresetsInput === 'object') {
                window.pixelPresets = pixelPresetsInput;
            }
        }
    } catch (e) {
    }
    domRootEl = domRoot || null;
    startSlogansCallback = typeof startSlogans === 'function' ? startSlogans : null;
    stopSlogansCallback = typeof stopSlogans === 'function' ? stopSlogans : null;
    assetsBaseUrl = assetsBaseUrlInput ? String(assetsBaseUrlInput) : '';
    gridData = Array.isArray(gridDataInput) ? gridDataInput : [];
    sectorColors = sectorColorsInput || {};
    sectorOrder = Array.isArray(sectorOrderInput) ? sectorOrderInput : [];
    setDetailFlag(false);
    if (typeof window !== 'undefined') {
        window.predictionsGridSetMotionDisabled = (next) => setMotionDisabled(next);
        window.predictionsGridTriggerMarquee = (msg) => {
            if (motionDisabled) return;
            if (isDetailView) return;
            if (activeSweepingEffect) return;
            const eff = effectPixelMarquee(msg);
            if (eff) activeSweepingEffect = eff;
        };
    }

    init({ canvasContainer });
}

export function predictionsGridDestroy() {
    gridIsRunning = false;
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }
    if (checkDataIntervalId) {
        clearInterval(checkDataIntervalId);
        checkDataIntervalId = null;
    }
    if (randomEffectsIntervalId) {
        clearInterval(randomEffectsIntervalId);
        randomEffectsIntervalId = null;
    }
    if (templateCycleInterval) {
        clearInterval(templateCycleInterval);
        templateCycleInterval = null;
    }
    if (hoverDebounceTimer) {
        clearTimeout(hoverDebounceTimer);
        hoverDebounceTimer = null;
    }

    if (pixelEditorUI) {
        try {
            if (pixelEditorUI.parentNode) pixelEditorUI.parentNode.removeChild(pixelEditorUI);
        } catch (e) {
        }
        pixelEditorUI = null;
    }

    try {
        (domRootEl || document.body).style.cursor = '';
    } catch (e) {
    }

    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('click', onMouseClick);
    window.removeEventListener('dblclick', onMouseDblClick);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('contextmenu', onContextMenu);

    if (mitigationBtnEl) mitigationBtnEl.removeEventListener('click', onMitigationButtonClick);
    if (gridBtnEl) gridBtnEl.removeEventListener('click', onGridButtonClick);
    if (prevBtnEl) prevBtnEl.removeEventListener('click', onPrevButtonClick);
    if (nextBtnEl) nextBtnEl.removeEventListener('click', onNextButtonClick);

    const hudMitigationToggleEl = getEl('hud-mitigation-toggle');
    if (hudMitigationToggleEl) hudMitigationToggleEl.removeEventListener('click', onMitigationButtonClick);
    mitigationBtnEl = null;
    gridBtnEl = null;
    prevBtnEl = null;
    nextBtnEl = null;

    try {
        if (renderer) {
            const el = renderer.domElement;
            if (el && el.parentNode) el.parentNode.removeChild(el);
            renderer.dispose();
        }
    } catch (e) {
    }

    renderer = null;
    scene = null;
    camera = null;
    panels = [];
    selectedPanel = null;
    gridData = [];
    sectorColors = {};
    sectorOrder = [];
    restoreRetroIcons();
    startSlogansCallback = null;
    stopSlogansCallback = null;
    assetsBaseUrl = '';

    try {
        if (domRootEl && domRootEl.dataset) {
            delete domRootEl.dataset.predictionsIsDetailView;
        }
    } catch (e) {
    }

    domRootEl = null;
    if (typeof window !== 'undefined') {
        if (hadWindowIsDetailView) window.isDetailView = prevWindowIsDetailView;
        else delete window.isDetailView;
        if (hadWindowPixelPresets) window.pixelPresets = prevWindowPixelPresets;
        else delete window.pixelPresets;
    }
    gridInitDone = false;
}
